import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CalendarDays, Copy, ExternalLink, MapPinned, Plus, Minus, X, ShoppingCart, Trash2 } from 'lucide-react';
import StoreLayout from '../../layouts/StoreLayout';
import { formatPrice } from '../../lib/mockData';
import { api } from '../../lib/api';
import { useCartStore } from '../../store/cartStore';
import type { PaymentMethod, Product, ProductOptionGroup, ProductOptionItem, ProductOptionSelectionRule, SelectedProductOption } from '../../types';
import { resolveAssetUrl } from '../../lib/assets';
import { cacheStoreBranding, deriveStoreBranding, getCachedStoreBranding } from '../../lib/storeBranding';
import Modal from '../../components/ui/Modal';
import DeliveryLocationPicker from '../../components/maps/DeliveryLocationPicker';
import ProductModelViewer from '../../components/ProductModelViewer';

type CheckoutOrderType = 'LOCAL' | 'PICKUP' | 'DELIVERY';
type DeliveryPaymentMethod = Extract<PaymentMethod, 'mercado_pago' | 'card_on_delivery' | 'cash'>;

const defaultDeliveryPaymentMethods: DeliveryPaymentMethod[] = ['mercado_pago', 'card_on_delivery', 'cash'];

const deliveryPaymentLabels: Record<DeliveryPaymentMethod, string> = {
    mercado_pago: 'Pagar agora',
    card_on_delivery: 'Cartao na entrega',
    cash: 'Dinheiro',
};

const orderStatusLabels: Record<string, string> = {
    AWAITING_PAYMENT: 'Aguardando pagamento',
    PENDING: 'Aguardando preparo',
    CONFIRMED: 'Confirmado',
    PREPARING: 'Em preparo',
    READY: 'Pronto',
    DELIVERED: 'Entregue',
    CANCELED: 'Cancelado',
};

const paymentStatusLabels: Record<string, string> = {
    PENDING: 'Pagamento pendente',
    PAID: 'Pago',
    FAILED: 'Falhou',
    EXPIRED: 'Expirado',
};

const orderTypeLabels: Record<string, string> = {
    LOCAL: 'Mesa',
    PICKUP: 'Retirada',
    DELIVERY: 'Delivery',
};

const toBackendPaymentMethod = (method: DeliveryPaymentMethod) => {
    switch (method) {
        case 'mercado_pago':
            return 'MERCADO_PAGO';
        case 'card_on_delivery':
            return 'CARD_ON_DELIVERY';
        case 'cash':
        default:
            return 'CASH';
    }
};

const getPendingOrderStorageKey = (slug: string) => `gastroflow_pending_order_${slug}`;
const getCustomerProfileStorageKey = (slug: string) => `gastroflow_customer_profile_${slug}`;

const safeReadPendingOrder = (slug: string) => {
    try {
        const raw = window.localStorage.getItem(getPendingOrderStorageKey(slug));
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        console.error('Failed to read pending order storage', error);
        return null;
    }
};

const safeReadCustomerProfile = (slug: string) => {
    try {
        const raw = window.localStorage.getItem(getCustomerProfileStorageKey(slug));
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        console.error('Failed to read customer profile storage', error);
        return null;
    }
};

const getStoredCustomerToken = (slug: string) => {
    const customerProfile = safeReadCustomerProfile(slug);
    if (customerProfile?.customerToken) {
        return customerProfile.customerToken as string;
    }

    const pendingOrder = safeReadPendingOrder(slug);
    if (pendingOrder?.customerToken) {
        return pendingOrder.customerToken as string;
    }

    return getOrCreateCustomerToken(slug);
};

const getOrCreateCustomerToken = (slug: string) => {
    const stored = safeReadCustomerProfile(slug);
    if (stored?.customerToken) {
        return stored.customerToken as string;
    }

    const nextToken = `cust_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(getCustomerProfileStorageKey(slug), JSON.stringify({
        customerToken: nextToken,
    }));
    return nextToken;
};

const persistCustomerProfile = (
    slug: string,
    payload: { customerToken: string; customerName?: string; customerPhone?: string },
) => {
    try {
        const current = safeReadCustomerProfile(slug) || {};
        window.localStorage.setItem(getCustomerProfileStorageKey(slug), JSON.stringify({
            ...current,
            customerToken: payload.customerToken,
            customerName: payload.customerName ?? current.customerName ?? '',
            customerPhone: payload.customerPhone ?? current.customerPhone ?? '',
        }));
    } catch (error) {
        console.error('Failed to persist customer profile storage', error);
    }
};

const buildDeliveryAddress = (parts: { street: string; number: string; neighborhood: string; city: string }) =>
    [parts.street.trim(), parts.number.trim(), parts.neighborhood.trim(), parts.city.trim()]
        .filter(Boolean)
        .join(', ');

const asArray = <T,>(value: T[] | null | undefined): T[] => Array.isArray(value) ? value : [];
const safeText = (value: unknown, fallback = '') => typeof value === 'string' ? value : fallback;

const renderStoreLoadingScreen = (branding?: {
    name?: string;
    logoUrl?: string;
    coverImageUrl?: string;
    tagline?: string;
    primaryColor?: string;
    accentColor?: string;
}) => {
    const resolvedLogo = resolveAssetUrl(branding?.logoUrl);
    const resolvedCover = resolveAssetUrl(branding?.coverImageUrl);
    const primaryColor = branding?.primaryColor || '#ff6b00';
    const accentColor = branding?.accentColor || '#ffb26b';

    return (
        <div className="min-h-screen overflow-hidden bg-[#080808] text-white">
            <div className="relative flex min-h-screen items-center justify-center px-6">
                {resolvedCover && (
                    <div className="absolute inset-0">
                        <img src={resolvedCover} alt="" className="h-full w-full object-cover opacity-20" />
                        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,8,8,0.4),rgba(8,8,8,0.95))]" />
                    </div>
                )}
                <div className="relative mx-auto w-full max-w-md rounded-[32px] border border-white/10 bg-white/[0.04] p-8 text-center shadow-[0_30px_90px_rgba(0,0,0,0.32)] backdrop-blur-xl">
                    {resolvedLogo ? (
                        <div className="mx-auto h-24 w-24 overflow-hidden rounded-[28px] border border-white/10 bg-black/25 shadow-[0_24px_50px_rgba(0,0,0,0.22)]">
                            <img src={resolvedLogo} alt="" className="h-full w-full object-cover" />
                        </div>
                    ) : (
                        <div
                            className="mx-auto flex h-24 w-24 items-center justify-center rounded-[28px] text-white shadow-[0_24px_50px_rgba(0,0,0,0.22)]"
                            style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` }}
                        >
                            <ShoppingCart className="h-9 w-9" />
                        </div>
                    )}
                    <div className="mt-6 text-[11px] font-black uppercase tracking-[0.28em]" style={{ color: accentColor }}>
                        Preparando a vitrine
                    </div>
                    <h1 className="mt-3 text-3xl font-black leading-tight text-white">
                        {branding?.name || 'Carregando cardapio'}
                    </h1>
                    <p className="mt-3 text-sm leading-6 text-white/68">
                        {branding?.tagline?.trim() || 'Estamos montando a loja publica do restaurante para voce navegar com a cara da marca.'}
                    </p>
                    <div className="mx-auto mt-7 flex h-2 w-40 overflow-hidden rounded-full bg-white/8">
                        <div
                            className="animate-pulse rounded-full"
                            style={{
                                width: '55%',
                                background: `linear-gradient(90deg, ${primaryColor}, ${accentColor})`,
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

const buildSelectionMap = (product?: Product | null) =>
    Object.fromEntries(
        asArray(product?.optionGroups).map((group) => [group.id, [] as string[]]),
    ) as Record<string, string[]>;

type EffectiveGroupRule = {
    includedQuantity: number;
    maxSelect?: number;
    visible: boolean;
};

const getGroupIdentityKey = (group: ProductOptionGroup) => group.ruleKey || group.id;

const buildEffectiveGroupRules = (
    product: Product | null,
    selectionMap: Record<string, string[]>,
) => {
    const rules: Record<string, EffectiveGroupRule> = {};
    const productGroups = asArray(product?.optionGroups);
    const sizeGroup = productGroups.find((group) => getGroupIdentityKey(group) === 'size');
    const selectedSizeIds = sizeGroup ? (selectionMap[sizeGroup.id] || []) : [];

    if (sizeGroup && selectedSizeIds.length === 0) {
        productGroups.forEach((group) => {
            const identityKey = getGroupIdentityKey(group);
            if (identityKey && identityKey !== 'size') {
                rules[identityKey] = {
                    includedQuantity: 0,
                    maxSelect: 0,
                    visible: false,
                };
            }
        });
    }

    productGroups.forEach((group) => {
        (selectionMap[group.id] || []).forEach((itemId) => {
            const item = asArray(group.items).find((candidate) => candidate.id === itemId);
            asArray(item?.selectionRules).forEach((rule: ProductOptionSelectionRule) => {
                if (!rule.targetGroupKey) return;

                const currentRule = rules[rule.targetGroupKey] || {
                    includedQuantity: 0,
                    visible: true,
                };

                rules[rule.targetGroupKey] = {
                    includedQuantity: typeof rule.includedQuantity === 'number'
                        ? rule.includedQuantity
                        : currentRule.includedQuantity,
                    maxSelect: typeof rule.maxSelectOverride === 'number'
                        ? rule.maxSelectOverride
                        : currentRule.maxSelect,
                    visible: typeof rule.visible === 'boolean' ? rule.visible : currentRule.visible,
                };
            });
        });
    });

    return rules;
};

const getEffectiveGroupConfig = (
    group: ProductOptionGroup,
    rules: Record<string, EffectiveGroupRule>,
) => {
    const rule = rules[getGroupIdentityKey(group)];
    return {
        visible: rule?.visible !== false,
        maxSelect: typeof rule?.maxSelect === 'number' ? rule.maxSelect : group.maxSelect,
        includedQuantity: rule?.includedQuantity || 0,
    };
};

const normalizeSelectionMap = (
    product: Product | null,
    selectionMap: Record<string, string[]>,
) => {
    if (!product) return selectionMap;

    const effectiveRules = buildEffectiveGroupRules(product, selectionMap);
    const normalizedEntries = asArray(product.optionGroups).map((group) => {
        const config = getEffectiveGroupConfig(group, effectiveRules);
        const selectedIds = selectionMap[group.id] || [];

        if (!config.visible) {
            return [group.id, [] as string[]] as const;
        }

        if (config.maxSelect <= 0) {
            return [group.id, [] as string[]] as const;
        }

        return [group.id, selectedIds.slice(0, config.maxSelect)] as const;
    });

    return Object.fromEntries(normalizedEntries) as Record<string, string[]>;
};

const getSelectedOptionsFromMap = (
    product: Product,
    selectionMap: Record<string, string[]>,
): SelectedProductOption[] => {
    const effectiveRules = buildEffectiveGroupRules(product, selectionMap);

    return asArray(product.optionGroups).flatMap((group) => {
        const groupConfig = getEffectiveGroupConfig(group, effectiveRules);
        if (!groupConfig.visible) {
            return [];
        }

        const visibleItems = (selectionMap[group.id] || [])
            .map((itemId) => asArray(group.items).find((item) => item.id === itemId))
            .filter((item): item is ProductOptionItem => !!item);

        if (group.pricingMode === 'EXTRA' && groupConfig.includedQuantity > 0) {
            const rankedIds = [...visibleItems]
                .sort((left, right) => right.price - left.price)
                .map((item) => item.id);

            return visibleItems.map((item) => {
                const rank = rankedIds.indexOf(item.id);
                const included = rank > -1 && rank < groupConfig.includedQuantity;
                return {
                    groupId: group.id,
                    groupName: group.name,
                    groupRuleKey: group.ruleKey,
                    pricingMode: group.pricingMode,
                    itemId: item.id,
                    itemName: item.name,
                    price: item.price,
                    effectivePrice: included ? 0 : item.price,
                    included,
                };
            });
        }

        return visibleItems.map((item) => ({
            groupId: group.id,
            groupName: group.name,
            groupRuleKey: group.ruleKey,
            pricingMode: group.pricingMode,
            itemId: item.id,
            itemName: item.name,
            price: item.price,
            effectivePrice: item.price,
            included: false,
        }));
    });
};

const getProductStartingPrice = (product: Product) => {
    const absolutePrices = asArray(product.optionGroups)
        .filter((group) => group.pricingMode === 'ABSOLUTE')
        .flatMap((group) => asArray(group.items).filter((item) => item.available !== false).map((item) => item.price))
        .filter((price) => Number.isFinite(price));

    if (absolutePrices.length > 0) {
        return Math.min(...absolutePrices);
    }

    return product.price;
};

const StorePage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [cachedBranding, setCachedBranding] = useState(() => slug ? getCachedStoreBranding(slug) : null);
    const [establishment, setEstablishment] = useState<any>(null);
    const [categories, setCategories] = useState<any[]>([]);
    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [selectedOptionMap, setSelectedOptionMap] = useState<Record<string, string[]>>({});
    const [qty, setQty] = useState(1);
    const [productModalError, setProductModalError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loadError, setLoadError] = useState('');
    const [orderType, setOrderType] = useState<CheckoutOrderType>('LOCAL');
    const [deliveryPaymentMethod, setDeliveryPaymentMethod] = useState<DeliveryPaymentMethod>('mercado_pago');
    const [checkoutData, setCheckoutData] = useState({
        customerName: '',
        tableNumber: '',
        customerPhone: '',
        deliveryStreet: '',
        deliveryNumber: '',
        deliveryNeighborhood: '',
        deliveryCity: '',
        cashNeedsChange: false,
        cashChangeFor: '',
    });
    const [checkoutError, setCheckoutError] = useState('');
    const [deliveryMapModalOpen, setDeliveryMapModalOpen] = useState(false);
    const [deliveryPoint, setDeliveryPoint] = useState<{ lat: number; lng: number } | null>(null);
    const [deliveryPointSource, setDeliveryPointSource] = useState<'device' | 'address' | 'map' | null>(null);
    const [deliveryCoverage, setDeliveryCoverage] = useState<{
        deliveryAvailable: boolean;
        matchedZoneId?: string;
        matchedZoneName?: string;
        deliveryFee?: number;
        pickupAvailable?: boolean;
        reason?: string;
        message?: string;
        lat?: number;
        lng?: number;
        geocodedAddress?: string;
        resolvedAddress?: {
            street?: string;
            number?: string;
            neighborhood?: string;
            city?: string;
            formattedAddress?: string;
        };
        addressResolutionFailed?: boolean;
    } | null>(null);
    const [deliveryCoverageChecking, setDeliveryCoverageChecking] = useState(false);
    const [deliveryLocationLoading, setDeliveryLocationLoading] = useState(false);
    const [reservationModalOpen, setReservationModalOpen] = useState(false);
    const [reservationSubmitting, setReservationSubmitting] = useState(false);
    const [reservationNotice, setReservationNotice] = useState('');
    const [reservationError, setReservationError] = useState('');
    const [reservationData, setReservationData] = useState({
        customerName: '',
        customerPhone: '',
        date: '',
        time: '',
        guests: '2',
        notes: '',
    });
    const [checkoutReturnState, setCheckoutReturnState] = useState<{
        orderId: string;
        shortId?: string;
        status: string;
        checkoutUrl?: string;
        pixCopyPasteCode?: string;
        pixQrCodeDataUrl?: string;
        pixExpiresAt?: string;
        total?: number;
    } | null>(null);
    const [checkoutNotice, setCheckoutNotice] = useState('');
    const [retryingPendingCheckout, setRetryingPendingCheckout] = useState(false);
    const [cancellingPendingDelivery, setCancellingPendingDelivery] = useState(false);
    const [copiedShortId, setCopiedShortId] = useState(false);
    const [copiedPixCode, setCopiedPixCode] = useState(false);
    const [latestOrderSummary, setLatestOrderSummary] = useState<{
        orderId: string;
        shortId?: string;
        total?: number;
        trackingUrl?: string;
    } | null>(null);
    const [customerOrders, setCustomerOrders] = useState<any[]>([]);
    const [trackShortId, setTrackShortId] = useState('');
    const [openingStatusPage, setOpeningStatusPage] = useState(false);
    const [openingTrackedOrderId, setOpeningTrackedOrderId] = useState<string | null>(null);
    const [resumingCheckoutOrderId, setResumingCheckoutOrderId] = useState<string | null>(null);
    const { addItem, items, total, isOpen, toggleCart, updateQuantity, removeItem, clearCart } = useCartStore();
    const cartCloseButtonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (!isOpen) return;

        const scrollY = window.scrollY;
        const previousBodyStyles = {
            overflow: document.body.style.overflow,
            position: document.body.style.position,
            top: document.body.style.top,
            width: document.body.style.width,
        };

        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                toggleCart();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        cartCloseButtonRef.current?.focus();

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousBodyStyles.overflow;
            document.body.style.position = previousBodyStyles.position;
            document.body.style.top = previousBodyStyles.top;
            document.body.style.width = previousBodyStyles.width;
            window.scrollTo(0, scrollY);
        };
    }, [isOpen, toggleCart]);

    useEffect(() => {
        if (!slug) return;
        setCachedBranding(getCachedStoreBranding(slug));
        api.get(`/establishments/${slug}`)
            .then((res) => {
                setLoadError('');
                setEstablishment(res.data);
                const nextCategories = asArray<any>(res.data?.categories);
                setCategories(nextCategories);
                const nextBranding = deriveStoreBranding(res.data);
                cacheStoreBranding(slug, nextBranding);
                setCachedBranding(nextBranding);
            })
            .catch((err) => {
                console.error(err);
                setLoadError('Cardapio indisponivel no momento.');
            });
    }, [slug]);

    useEffect(() => {
        if (!slug) return;

        const stored = safeReadCustomerProfile(slug);
        if (!stored) return;

        setCheckoutData((current) => ({
            ...current,
            customerName: current.customerName || stored.customerName || '',
            customerPhone: current.customerPhone || stored.customerPhone || '',
        }));
        getOrCreateCustomerToken(slug);
    }, [slug]);

    useEffect(() => {
        if (!slug || !establishment?.id) return;

        const customerToken = getStoredCustomerToken(slug);
        let active = true;

        const loadCustomerOrders = () => {
            api.get(`/orders/public/establishment/${establishment.id}/history`, {
                params: { customerToken },
            })
                .then(({ data }) => {
                    if (active) {
                        setCustomerOrders(asArray(data));
                    }
                })
                .catch((error) => {
                    console.error('Failed to load customer order history', error);
                });
        };

        loadCustomerOrders();
        const interval = window.setInterval(loadCustomerOrders, 15000);

        return () => {
            active = false;
            window.clearInterval(interval);
        };
    }, [establishment?.id, slug]);

    useEffect(() => {
        if (!slug) return;

        const paymentResult = searchParams.get('payment');
        const orderIdFromQuery = searchParams.get('order');
        const externalReference = searchParams.get('external_reference');
        const preferenceId = searchParams.get('preference_id');
        const stored = safeReadPendingOrder(slug);
        const orderId = orderIdFromQuery || externalReference || stored?.orderId;

        if (!orderId) {
            return;
        }

        const customerToken = getStoredCustomerToken(slug);

        api.get(`/orders/public/${orderId}`, {
            params: { customerToken },
        })
            .then(({ data }) => {
                if (data.paymentMethod !== 'MERCADO_PAGO') {
                    return;
                }

                if (data.paymentStatus === 'PAID' || data.status === 'CONFIRMED') {
                    window.localStorage.removeItem(getPendingOrderStorageKey(slug));
                    setCheckoutReturnState(null);
                    setCheckoutNotice('Pagamento confirmado. O restaurante ja recebeu seu pedido.');
                    setLatestOrderSummary({
                        orderId: data.id,
                        shortId: data.shortId,
                        total: data.total,
                        trackingUrl: `${window.location.origin}/store/${slug}/pedido/${data.shortId}`,
                    });
                } else {
                    window.localStorage.setItem(getPendingOrderStorageKey(slug), JSON.stringify({
                        orderId: data.id,
                        checkoutUrl: data.mercadoPagoCheckoutUrl,
                        total: data.total,
                        customerToken,
                        preferenceId: data.mercadoPagoPreferenceId || preferenceId || stored?.preferenceId,
                    }));
                    setCheckoutReturnState({
                        orderId: data.id,
                        shortId: data.shortId,
                        status: paymentResult || data.paymentStatus || data.status,
                        checkoutUrl: data.mercadoPagoCheckoutUrl,
                        pixCopyPasteCode: data.pixCopyPasteCode,
                        pixQrCodeDataUrl: data.pixQrCodeDataUrl,
                        pixExpiresAt: data.pixExpiresAt,
                        total: data.total,
                    });
                    setLatestOrderSummary({
                        orderId: data.id,
                        shortId: data.shortId,
                        total: data.total,
                        trackingUrl: `${window.location.origin}/store/${slug}/pedido/${data.shortId}`,
                    });
                    setCheckoutNotice(
                        data.pixCopyPasteCode
                            ? 'Seu Pix foi gerado. Copie o codigo ou escaneie o QR Code para concluir o pagamento.'
                            : !data.mercadoPagoCheckoutUrl
                            ? 'Seu pedido foi salvo, mas o Pix ainda nao foi gerado. Gere um novo Pix abaixo para continuar.'
                            : paymentResult === 'failure'
                            ? 'O pagamento nao foi concluido. Voce pode retomar o checkout abaixo.'
                            : 'Seu pedido esta aguardando pagamento para ser confirmado.'
                    );
                }

                if (paymentResult || externalReference || preferenceId) {
                    const nextParams = new URLSearchParams(searchParams);
                    nextParams.delete('payment');
                    nextParams.delete('order');
                    nextParams.delete('collection_id');
                    nextParams.delete('collection_status');
                    nextParams.delete('payment_id');
                    nextParams.delete('status');
                    nextParams.delete('external_reference');
                    nextParams.delete('payment_type');
                    nextParams.delete('merchant_order_id');
                    nextParams.delete('preference_id');
                    nextParams.delete('site_id');
                    nextParams.delete('processing_mode');
                    nextParams.delete('merchant_account_id');
                    setSearchParams(nextParams, { replace: true });
                }
            })
            .catch((error) => {
                console.error('Failed to recover checkout state', error);
            });
    }, [searchParams, setSearchParams, slug]);

    const enabledCheckoutPaymentMethods = useMemo<DeliveryPaymentMethod[]>(() => {
        const configuredMethods = establishment?.settings?.deliveryPaymentMethods;
        if (!Array.isArray(configuredMethods) || configuredMethods.length === 0) {
            return defaultDeliveryPaymentMethods;
        }

        return configuredMethods
            .map((method: string) => method.toLowerCase())
            .filter((method: string): method is DeliveryPaymentMethod =>
                method === 'mercado_pago' || method === 'card_on_delivery' || method === 'cash',
            );
    }, [establishment?.settings]);

    const deliveryZones = useMemo(
        () => Array.isArray(establishment?.deliveryZones) ? establishment.deliveryZones : [],
        [establishment?.deliveryZones],
    );

    const deliveryMapCenter = useMemo(
        () => ({
            lat: Number(establishment?.settings?.deliveryMapCenterLat) || -19.9167,
            lng: Number(establishment?.settings?.deliveryMapCenterLng) || -43.9345,
        }),
        [establishment?.settings?.deliveryMapCenterLat, establishment?.settings?.deliveryMapCenterLng],
    );

    const deliveryEnabled = establishment?.settings?.deliveryEnabled !== false;
    const pickupEnabled = establishment?.settings?.pickupEnabled !== false;
    const deliveryFixedFee = useMemo(
        () => Math.max(0, Number(establishment?.settings?.deliveryFixedFee) || 0),
        [establishment?.settings?.deliveryFixedFee],
    );
    const checkoutSubtotal = total();
    const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    const checkoutDeliveryFee = useMemo(() => {
        if (orderType !== 'DELIVERY') {
            return 0;
        }

        const zoneFee = Number(deliveryCoverage?.deliveryFee);
        if (deliveryCoverage?.deliveryAvailable && Number.isFinite(zoneFee) && zoneFee >= 0) {
            return zoneFee;
        }

        return deliveryFixedFee;
    }, [deliveryCoverage?.deliveryAvailable, deliveryCoverage?.deliveryFee, deliveryFixedFee, orderType]);
    const checkoutTotal = checkoutSubtotal + checkoutDeliveryFee;

    const reservationsEnabled = establishment?.settings?.hasReservationModule !== false
        && establishment?.settings?.hasTableModule !== false
        && establishment?.settings?.hasReservations !== false;

    useEffect(() => {
        if (orderType !== 'DELIVERY' && orderType !== 'PICKUP') return;
        if (enabledCheckoutPaymentMethods.length === 0) {
            setCheckoutError(orderType === 'PICKUP'
                ? 'Este restaurante ainda nao configurou formas de pagamento para retirada.'
                : 'Este restaurante ainda nao configurou formas de pagamento para delivery.');
            return;
        }

        if (!enabledCheckoutPaymentMethods.includes(deliveryPaymentMethod)) {
            setDeliveryPaymentMethod(enabledCheckoutPaymentMethods[0]);
        }
    }, [deliveryPaymentMethod, enabledCheckoutPaymentMethods, orderType]);

    useEffect(() => {
        if (orderType !== 'DELIVERY') {
            setDeliveryCoverage(null);
        }
    }, [orderType]);

    useEffect(() => {
        if (orderType === 'DELIVERY' && !deliveryEnabled) {
            setOrderType(pickupEnabled ? 'PICKUP' : 'LOCAL');
        }

        if (orderType === 'PICKUP' && !pickupEnabled) {
            setOrderType(deliveryEnabled ? 'DELIVERY' : 'LOCAL');
        }
    }, [deliveryEnabled, orderType, pickupEnabled]);

    const publicCategories = useMemo(
        () =>
            categories
                .map((category) => ({
                    ...category,
                    products: asArray(category.products).filter((product: any) => product?.available !== false),
                }))
                .filter((category) => asArray(category.products).length > 0),
        [categories],
    );

    useEffect(() => {
        if (publicCategories.length === 0) {
            setActiveCategory(null);
            return;
        }

        if (!activeCategory || !publicCategories.some((category) => category.id === activeCategory)) {
            setActiveCategory(publicCategories[0].id);
        }
    }, [activeCategory, publicCategories]);

    const visibleCategory = useMemo(() => {
        const category = publicCategories.find((cat) => cat.id === activeCategory);
        if (!category) return null;

        const term = searchTerm.trim().toLowerCase();
        if (!term) return category;

        return {
            ...category,
            products: asArray(category.products).filter((product: any) =>
                safeText(product?.name).toLowerCase().includes(term)
                || safeText(product?.description).toLowerCase().includes(term),
            ),
        };
    }, [activeCategory, publicCategories, searchTerm]);

    const selectedProductOptions = useMemo(
        () => (selectedProduct ? getSelectedOptionsFromMap(selectedProduct, selectedOptionMap) : []),
        [selectedOptionMap, selectedProduct],
    );

    const selectedProductEffectiveRules = useMemo(
        () => (selectedProduct ? buildEffectiveGroupRules(selectedProduct, selectedOptionMap) : {}),
        [selectedOptionMap, selectedProduct],
    );

    const selectedSizeLabel = useMemo(() => {
        if (!selectedProduct) return '';

        const sizeGroup = asArray(selectedProduct.optionGroups).find((group) => getGroupIdentityKey(group) === 'size');
        if (!sizeGroup) return '';

        const selectedSizeId = (selectedOptionMap[sizeGroup.id] || [])[0];
        if (!selectedSizeId) return '';

        return asArray(sizeGroup.items).find((item) => item.id === selectedSizeId)?.name || '';
    }, [selectedOptionMap, selectedProduct]);

    const hasHiddenDependentGroups = useMemo(() => {
        if (!selectedProduct) return false;

        const groups = asArray(selectedProduct.optionGroups);
        const hasSizeGroup = groups.some((group) => getGroupIdentityKey(group) === 'size');
        const hasDependentGroups = groups.some((group) => {
            const identityKey = getGroupIdentityKey(group);
            return identityKey && identityKey !== 'size';
        });

        return hasSizeGroup && hasDependentGroups && !selectedSizeLabel;
    }, [selectedProduct, selectedSizeLabel]);

    const selectedProductOptionLookup = useMemo(
        () => Object.fromEntries(
            selectedProductOptions.map((option) => [`${option.groupId}:${option.itemId}`, option]),
        ) as Record<string, SelectedProductOption>,
        [selectedProductOptions],
    );

    const selectedProductUnitPrice = useMemo(
        () => {
            if (!selectedProduct) return 0;

            const absoluteOptions = selectedProductOptions.filter((option) => option.pricingMode === 'ABSOLUTE');
            const extraOptions = selectedProductOptions.filter((option) => option.pricingMode === 'EXTRA');
            const basePrice = absoluteOptions.length > 0
                ? absoluteOptions.reduce((sum, option) => sum + option.price, 0)
                : getProductStartingPrice(selectedProduct);

            return basePrice + extraOptions.reduce((sum, option) => sum + (option.effectivePrice ?? option.price), 0);
        },
        [selectedProduct, selectedProductOptions],
    );

    const selectedProductValidationError = useMemo(() => {
        if (!selectedProduct) return '';

        const missingGroup = asArray(selectedProduct.optionGroups).find((group) => {
            const groupConfig = getEffectiveGroupConfig(group, selectedProductEffectiveRules);
            return group.required && groupConfig.visible && (selectedOptionMap[group.id] || []).length === 0;
        });
        if (missingGroup) {
            return `Escolha uma opcao em ${missingGroup.name}.`;
        }

        return '';
    }, [selectedOptionMap, selectedProduct, selectedProductEffectiveRules]);

    const checkDeliveryCoverage = async (
        payload: {
            lat?: number;
            lng?: number;
            address?: string;
            street?: string;
            number?: string;
            neighborhood?: string;
            city?: string;
        },
        options?: {
            preserveConfirmedPointOnAddressCheck?: boolean;
            pointSource?: 'device' | 'address' | 'map';
        },
    ) => {
        if (!establishment?.id) return null;

        try {
            setDeliveryCoverageChecking(true);
            setCheckoutError('');
            const { data } = await api.post(`/establishments/${establishment.id}/check-delivery-area`, payload);

            const shouldPreserveConfirmedPoint =
                options?.preserveConfirmedPointOnAddressCheck
                && !!payload.address
                && !!deliveryPoint
                && !!deliveryCoverage?.deliveryAvailable
                && !data?.deliveryAvailable;

            if (shouldPreserveConfirmedPoint) {
                setCheckoutError('Sua localizacao confirmada continua dentro da area. O endereco digitado nao foi localizado com precisao no mapa.');
                return deliveryCoverage;
            }

            setDeliveryCoverage(data);
            if (typeof data?.lat === 'number' && typeof data?.lng === 'number') {
                setDeliveryPoint({ lat: data.lat, lng: data.lng });
                if (options?.pointSource) {
                    setDeliveryPointSource(options.pointSource);
                }
            }
            if (data?.resolvedAddress) {
                setCheckoutData((current) => ({
                    ...current,
                    deliveryStreet: data.resolvedAddress.street || current.deliveryStreet,
                    deliveryNumber: data.resolvedAddress.number || current.deliveryNumber,
                    deliveryNeighborhood: data.resolvedAddress.neighborhood || current.deliveryNeighborhood,
                    deliveryCity: data.resolvedAddress.city || current.deliveryCity,
                }));
            }
            return data;
        } catch (error: any) {
            console.error('Failed to validate delivery area', error);
            const message = error?.response?.data?.message || 'Nao foi possivel validar esse endereco agora.';
            setCheckoutError(message);
            const fallback = {
                deliveryAvailable: false,
                pickupAvailable: pickupEnabled,
                reason: 'validation_failed',
                message,
            };
            setDeliveryCoverage(fallback);
            return fallback;
        } finally {
            setDeliveryCoverageChecking(false);
        }
    };

    const useCurrentDeviceLocation = () => {
        if (!window.isSecureContext) {
            setCheckoutError('A localização só pode ser usada em uma conexão segura (HTTPS). Informe o endereço manualmente.');
            return;
        }

        if (!('geolocation' in navigator)) {
            setCheckoutError('Não foi possível obter sua localização. Informe o endereço ou marque o ponto no mapa.');
            return;
        }

        setDeliveryLocationLoading(true);
        setCheckoutError('');

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const coords = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };

                setDeliveryPoint(coords);
                setDeliveryPointSource('device');
                setDeliveryCoverage(null);
                try {
                    const coverage = await checkDeliveryCoverage(coords, { pointSource: 'device' });
                    if (coverage?.reason !== 'validation_failed') {
                        const resolvedAddress = coverage?.resolvedAddress;
                        const hasAddressFields = !!(
                            resolvedAddress?.street
                            || resolvedAddress?.neighborhood
                            || resolvedAddress?.city
                        );

                        if (coverage?.addressResolutionFailed || !hasAddressFields) {
                            setCheckoutError('Localização encontrada, mas não conseguimos preencher o endereço automaticamente.');
                        } else if (!resolvedAddress?.number) {
                            setCheckoutError('Localização encontrada. Confira o endereço e informe o número do local.');
                        }
                    }
                } finally {
                    setDeliveryLocationLoading(false);
                }
            },
            (error) => {
                setDeliveryLocationLoading(false);
                if (error.code === error.PERMISSION_DENIED) {
                    setCheckoutError('Permissão de localização negada.');
                    return;
                }

                setCheckoutError('Não foi possível obter sua localização.');
            },
            {
                enableHighAccuracy: true,
                timeout: 12000,
                maximumAge: 60000,
            },
        );
    };

    const handleAdd = () => {
        if (!selectedProduct) return;
        if (selectedProductValidationError) {
            setProductModalError(selectedProductValidationError);
            return;
        }
        addItem(selectedProduct, qty, selectedProductOptions, selectedProductUnitPrice);
        setSelectedProduct(null);
        setSelectedOptionMap({});
        setQty(1);
        setProductModalError('');
    };

    const openProductModal = (product: Product) => {
        setSelectedProduct(product);
        setSelectedOptionMap(normalizeSelectionMap(product, buildSelectionMap(product)));
        setQty(1);
        setProductModalError('');
    };

    const closeProductModal = () => {
        setSelectedProduct(null);
        setSelectedOptionMap({});
        setQty(1);
        setProductModalError('');
    };

    const toggleOptionSelection = (group: ProductOptionGroup, itemId: string) => {
        setProductModalError('');
        setSelectedOptionMap((current) => {
            if (!selectedProduct) return current;

            const effectiveRules = buildEffectiveGroupRules(selectedProduct, current);
            const groupConfig = getEffectiveGroupConfig(group, effectiveRules);
            const selectedIds = current[group.id] || [];
            const alreadySelected = selectedIds.includes(itemId);

            if (!groupConfig.visible) {
                return current;
            }

            if (groupConfig.maxSelect <= 1) {
                return normalizeSelectionMap(selectedProduct, {
                    ...current,
                    [group.id]: alreadySelected ? [] : [itemId],
                });
            }

            if (alreadySelected) {
                return normalizeSelectionMap(selectedProduct, {
                    ...current,
                    [group.id]: selectedIds.filter((selectedId) => selectedId !== itemId),
                });
            }

            if (selectedIds.length >= groupConfig.maxSelect) {
                return current;
            }

            return normalizeSelectionMap(selectedProduct, {
                ...current,
                [group.id]: [...selectedIds, itemId],
            });
        });
    };

    const resetCheckout = () => {
        clearCart();
        setCheckoutData({
            customerName: '',
            tableNumber: '',
            customerPhone: '',
            deliveryStreet: '',
            deliveryNumber: '',
            deliveryNeighborhood: '',
            deliveryCity: '',
            cashNeedsChange: false,
            cashChangeFor: '',
        });
        setDeliveryPoint(null);
        setDeliveryPointSource(null);
        setDeliveryCoverage(null);
        setOrderType('LOCAL');
        setDeliveryPaymentMethod('mercado_pago');
    };

    const handleCheckout = async () => {
        if (!establishment || items.length === 0) return;
        const customerName = checkoutData.customerName.trim();
        const tableNumber = checkoutData.tableNumber.trim();
        const customerPhone = checkoutData.customerPhone.trim();
        const deliveryAddress = buildDeliveryAddress({
            street: checkoutData.deliveryStreet,
            number: checkoutData.deliveryNumber,
            neighborhood: checkoutData.deliveryNeighborhood,
            city: checkoutData.deliveryCity,
        });
        const cashChangeFor = checkoutData.cashChangeFor.trim();
        const customerToken = slug ? getStoredCustomerToken(slug) : undefined;

        if (!customerName) {
            setCheckoutError('Preencha o nome para finalizar o pedido.');
            return;
        }

        if (orderType === 'LOCAL' && !tableNumber) {
            setCheckoutError('Preencha o numero da mesa para finalizar o pedido.');
            return;
        }

        if (
            orderType === 'DELIVERY' &&
            (!customerPhone || !checkoutData.deliveryStreet.trim() || !checkoutData.deliveryNumber.trim() || !checkoutData.deliveryNeighborhood.trim() || !checkoutData.deliveryCity.trim())
        ) {
            setCheckoutError('Preencha telefone, rua, numero, bairro e cidade para pedidos delivery.');
            return;
        }

        if (orderType === 'DELIVERY' && !deliveryEnabled) {
            setCheckoutError('Este restaurante esta com o delivery pausado no momento.');
            return;
        }

        if ((orderType === 'DELIVERY' || orderType === 'PICKUP') && enabledCheckoutPaymentMethods.length === 0) {
            setCheckoutError(orderType === 'PICKUP'
                ? 'Retirada indisponivel: restaurante sem meios de pagamento configurados.'
                : 'Delivery indisponivel: restaurante sem meios de pagamento configurados.');
            return;
        }

        if ((orderType === 'DELIVERY' || orderType === 'PICKUP') && deliveryPaymentMethod === 'cash' && checkoutData.cashNeedsChange && !cashChangeFor) {
            setCheckoutError('Informe o valor para troco.');
            return;
        }

        try {
            setIsSubmitting(true);
            setCheckoutError('');
            const currentCoverage = orderType === 'DELIVERY'
                ? await checkDeliveryCoverage(
                    deliveryPoint
                        ? deliveryPoint
                        : { address: deliveryAddress },
                )
                : null;

            if (orderType === 'DELIVERY' && (!currentCoverage || !currentCoverage.deliveryAvailable)) {
                if (currentCoverage?.reason === 'validation_failed') {
                    setCheckoutError(currentCoverage.message || 'Nao foi possivel validar esse endereco agora.');
                } else if (currentCoverage?.pickupAvailable) {
                    setCheckoutError('Esse endereco esta fora da area de entrega. Troque para retirada no local para continuar.');
                } else {
                    setCheckoutError('Esse endereco esta fora da area de entrega deste restaurante.');
                }
                return;
            }
            const payload = {
                type: orderType,
                customerName,
                customerToken,
                tableNumber: orderType === 'LOCAL' ? tableNumber : undefined,
                customerPhone: customerPhone || undefined,
                deliveryAddress: orderType === 'DELIVERY' ? deliveryAddress : undefined,
                customerLat: orderType === 'DELIVERY' ? deliveryPoint?.lat : undefined,
                customerLng: orderType === 'DELIVERY' ? deliveryPoint?.lng : undefined,
                paymentMethod: orderType === 'DELIVERY' || orderType === 'PICKUP' ? toBackendPaymentMethod(deliveryPaymentMethod) : undefined,
                cashNeedsChange: (orderType === 'DELIVERY' || orderType === 'PICKUP') && deliveryPaymentMethod === 'cash' ? checkoutData.cashNeedsChange : undefined,
                cashChangeFor:
                    (orderType === 'DELIVERY' || orderType === 'PICKUP') && deliveryPaymentMethod === 'cash' && checkoutData.cashNeedsChange
                        ? Number(cashChangeFor)
                        : undefined,
                establishmentId: establishment.id,
                items: items.map((item) => ({
                    productId: item.product.id,
                    quantity: item.quantity,
                    price: item.unitPrice,
                    selectedOptions: item.selectedOptions.map((option) => ({
                        groupId: option.groupId,
                        itemId: option.itemId,
                    })),
                    notes: item.selectedOptions.length > 0
                        ? item.selectedOptions.map((option) => `${option.groupName}: ${option.itemName}`).join(' | ')
                        : undefined,
                })),
            };
            const { data } = await api.post('/orders', payload);
            setCustomerOrders((current) => [data, ...current.filter((order) => order.id !== data.id)].slice(0, 10));
            setLatestOrderSummary({
                orderId: data.id,
                shortId: data.shortId,
                total: data.total,
                trackingUrl: slug ? `${window.location.origin}/store/${slug}/pedido/${data.shortId}` : undefined,
            });

            if (slug && customerToken) {
                persistCustomerProfile(slug, {
                    customerToken,
                    customerName,
                    customerPhone: customerPhone || undefined,
                });
            }

            if ((orderType === 'DELIVERY' || orderType === 'PICKUP') && deliveryPaymentMethod === 'mercado_pago') {
                if (slug) {
                    window.localStorage.setItem(getPendingOrderStorageKey(slug), JSON.stringify({
                        orderId: data.id,
                        checkoutUrl: data.mercadoPagoCheckoutUrl,
                        total: data.total,
                        customerToken,
                    }));
                }
                resetCheckout();
                if (isOpen) {
                    toggleCart();
                }
                setCheckoutReturnState({
                    orderId: data.id,
                    shortId: data.shortId,
                    status: data.paymentStatus || data.status || 'pending',
                    checkoutUrl: data.mercadoPagoCheckoutUrl,
                    pixCopyPasteCode: data.pixCopyPasteCode,
                    pixQrCodeDataUrl: data.pixQrCodeDataUrl,
                    pixExpiresAt: data.pixExpiresAt,
                    total: data.total,
                });
                setCheckoutNotice(
                    data.pixCopyPasteCode
                        ? 'Pix gerado com sucesso. Pague com o QR Code ou copie o codigo abaixo.'
                        : data.checkoutError
                            ? `${data.checkoutError} Clique em "Gerar Pix" para tentar novamente.`
                            : 'Seu pedido foi salvo e esta aguardando a geracao do Pix. Clique em "Gerar Pix" para continuar.'
                );
            } else {
                setCheckoutNotice('Pedido confirmado. Use o codigo abaixo para acompanhar o status.');
                resetCheckout();
                if (isOpen) {
                    toggleCart();
                }
            }
        } catch (error) {
            console.error(error);
            setCheckoutError('Erro ao realizar pedido. Confira os dados e tente novamente.');
            alert('Erro ao realizar pedido. Tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReservationSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!establishment?.id) return;

        try {
            setReservationSubmitting(true);
            setReservationError('');
            setReservationNotice('');

            await api.post('/reservations', {
                customerName: reservationData.customerName,
                customerPhone: reservationData.customerPhone,
                guests: Number(reservationData.guests),
                notes: reservationData.notes || undefined,
                establishmentId: establishment.id,
                date: new Date(`${reservationData.date}T${reservationData.time}:00`).toISOString(),
            });

            setReservationModalOpen(false);
            setReservationData({
                customerName: '',
                customerPhone: '',
                date: '',
                time: '',
                guests: '2',
                notes: '',
            });
            setReservationNotice('Reserva enviada com sucesso. O restaurante vai confirmar os detalhes em breve.');
        } catch (error: any) {
            console.error(error);
            setReservationError(error?.response?.data?.message || 'Nao foi possivel enviar a reserva.');
        } finally {
            setReservationSubmitting(false);
        }
    };

    const cancelPendingDelivery = async () => {
        if (!checkoutReturnState?.orderId || !slug) return;

        try {
            setCancellingPendingDelivery(true);
            const customerToken = getStoredCustomerToken(slug);

            await api.post(`/orders/${checkoutReturnState.orderId}/cancel-pending-payment`, { customerToken });
            window.localStorage.removeItem(getPendingOrderStorageKey(slug));
            setCheckoutReturnState(null);
            setCheckoutNotice('Delivery cancelado antes da confirmacao de pagamento.');
        } catch (error: any) {
            console.error('Failed to cancel pending delivery', error);

            const status = error?.response?.status;
            const message = error?.response?.data?.message;

            if (status === 400 || status === 404) {
                try {
                    const customerToken = getStoredCustomerToken(slug);
                    const { data } = await api.get(`/orders/public/${checkoutReturnState.orderId}`, {
                        params: { customerToken },
                    });
                    if (data.status === 'CANCELED' || data.status === 'CONFIRMED' || data.paymentStatus === 'PAID') {
                        window.localStorage.removeItem(getPendingOrderStorageKey(slug));
                        setCheckoutReturnState(null);
                        setCheckoutNotice(
                            data.status === 'CANCELED'
                                ? 'Esse delivery ja foi cancelado.'
                                : 'Esse pedido ja saiu do estado pendente de pagamento.'
                        );
                        return;
                    }
                } catch (reloadError) {
                    console.error('Failed to reload pending order after cancel conflict', reloadError);
                }
            }

            setCheckoutNotice(message || 'Nao foi possivel cancelar o delivery agora. Tente novamente em instantes.');
        } finally {
            setCancellingPendingDelivery(false);
        }
    };

    const retryPendingCheckout = async () => {
        if (!checkoutReturnState?.orderId || !slug) return;

        try {
            setRetryingPendingCheckout(true);
            const customerToken = getStoredCustomerToken(slug);
            const { data } = await api.post(`/orders/${checkoutReturnState.orderId}/retry-mercadopago-checkout`, { customerToken });
            window.localStorage.setItem(getPendingOrderStorageKey(slug), JSON.stringify({
                orderId: data.id,
                checkoutUrl: data.mercadoPagoCheckoutUrl,
                total: data.total,
                customerToken,
                preferenceId: data.mercadoPagoPreferenceId,
            }));
            setCheckoutReturnState({
                orderId: data.id,
                shortId: data.shortId,
                status: data.paymentStatus || data.status || 'pending',
                checkoutUrl: data.mercadoPagoCheckoutUrl,
                pixCopyPasteCode: data.pixCopyPasteCode,
                pixQrCodeDataUrl: data.pixQrCodeDataUrl,
                pixExpiresAt: data.pixExpiresAt,
                total: data.total,
            });
            setCheckoutNotice(
                data.pixCopyPasteCode
                    ? 'Novo Pix gerado. Use o QR Code ou copie o codigo para pagar.'
                    : 'Nao foi possivel gerar o Pix agora. Tente novamente em instantes.'
            );
        } catch (error) {
            console.error('Failed to retry Mercado Pago checkout', error);
            setCheckoutNotice('Nao foi possivel gerar o Pix agora. Tente novamente em instantes.');
        } finally {
            setRetryingPendingCheckout(false);
        }
    };

    const storeBranding = establishment ? deriveStoreBranding(establishment) : cachedBranding;
    const brandPrimary = storeBranding?.primaryColor || '#ff6b00';
    const brandAccent = storeBranding?.accentColor || '#ffb26b';
    const brandSoftBorder = `${brandPrimary}33`;
    const brandSoftBackground = `${brandPrimary}14`;
    const showcaseProducts = useMemo(
        () =>
            publicCategories
                .flatMap((category: any) => asArray(category.products))
                .filter((product: any) => product?.available !== false)
                .slice(0, 4),
        [publicCategories],
    );
    const quickTrackShortId = latestOrderSummary?.shortId || customerOrders[0]?.shortId || '';
    const headerActionLabel = quickTrackShortId
        ? 'Acompanhar pedido'
        : items.length > 0
            ? 'Ver carrinho'
            : undefined;
    const handleHeaderAction = () => {
        if (quickTrackShortId && slug) {
            navigate(`/store/${slug}/pedido/${quickTrackShortId}`);
            return;
        }

        if (!isOpen) {
            toggleCart();
        }
    };

    if (!establishment) {
        if (loadError) {
            return <div className="text-white text-center p-12">{loadError}</div>;
        }
        return renderStoreLoadingScreen(storeBranding || undefined);
    }

    return (
        <StoreLayout
            slug={slug!}
            establishmentName={establishment.name}
            logoUrl={establishment.logoUrl}
            coverImageUrl={storeBranding?.coverImageUrl}
            tagline={storeBranding?.tagline}
            primaryColor={storeBranding?.primaryColor}
            accentColor={storeBranding?.accentColor}
            themePreset={storeBranding?.themePreset}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            headerActionLabel={headerActionLabel}
            onHeaderAction={handleHeaderAction}
            showHero={false}
        >
            <section className="mb-5 overflow-hidden rounded-[28px] border border-white/10 bg-white/[0.04]">
                <div className="flex items-center gap-4 p-4">
                    {establishment.logoUrl ? (
                        <img
                            src={resolveAssetUrl(establishment.logoUrl)}
                            alt=""
                            className="h-14 w-14 shrink-0 rounded-2xl border border-white/10 object-cover"
                        />
                    ) : (
                        <div
                            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white"
                            style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandAccent})` }}
                        >
                            <ShoppingCart className="h-6 w-6" />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: brandAccent }}>
                            Cardapio aberto
                        </p>
                        <h1 className="mt-1 truncate text-xl font-black text-white">{establishment.name}</h1>
                        <p className="mt-1 line-clamp-2 text-sm leading-5 text-white/65">
                            {storeBranding?.tagline?.trim() || 'Escolha seus itens favoritos e finalize o pedido pelo site.'}
                        </p>
                    </div>
                </div>
            </section>

            <div className="sticky top-16 z-30 -mx-4 mb-5 border-y border-white/10 bg-[#080808]/92 px-4 py-3 backdrop-blur-2xl">
                <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
                    {publicCategories.map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => setActiveCategory(cat.id)}
                            className={`flex-shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-all ${activeCategory === cat.id ? 'text-white shadow-glow-sm' : 'border-white/10 bg-white/[0.04] text-muted-foreground hover:text-white'}`}
                            style={activeCategory === cat.id ? {
                                background: `linear-gradient(135deg, ${storeBranding?.primaryColor || '#ff6b00'}, ${storeBranding?.accentColor || '#ffb26b'})`,
                                borderColor: 'transparent',
                            } : undefined}
                        >
                            {cat.name}
                        </button>
                    ))}
                </div>
            </div>

            {visibleCategory && (
                <section className="mb-8">
                    <div className="mb-4 flex items-end justify-between gap-4">
                        <div>
                            <p className="text-xs uppercase tracking-[0.22em]" style={{ color: brandAccent }}>Escolha agora</p>
                            <h2 className="mt-1 text-2xl font-black text-white">{visibleCategory.name}</h2>
                        </div>
                        <span className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-semibold text-white/65">
                            {asArray(visibleCategory.products).length} itens
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                        {asArray(visibleCategory.products).map((product: any) => (
                            <button
                                key={product.id}
                                onClick={() => openProductModal(product)}
                                disabled={!product.available}
                                className={`group overflow-hidden rounded-2xl border border-white/10 bg-[#101010] text-left shadow-[0_12px_30px_rgba(0,0,0,0.18)] transition-all duration-200 ${product.available ? 'cursor-pointer hover:-translate-y-0.5 hover:border-primary/35' : 'cursor-not-allowed opacity-60'} `}
                            >
                                <div className="relative aspect-[4/3] bg-[#171717]">
                                    {product.imageUrl ? (
                                        <img src={resolveAssetUrl(product.imageUrl)} alt="" className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.04]" />
                                    ) : (
                                        <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">IMG</div>
                                    )}
                                    {product.model3dEnabled && product.model3dGlbUrl && (
                                        <span
                                            className="absolute left-2 top-2 rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-wide"
                                            style={{
                                                borderColor: `${brandPrimary}33`,
                                                backgroundColor: `${brandPrimary}1a`,
                                                color: brandPrimary,
                                            }}
                                        >
                                            3D / AR
                                        </span>
                                    )}
                                </div>
                                <div className="space-y-2 p-3">
                                    <p className="line-clamp-2 min-h-[36px] text-sm font-bold leading-tight text-white">
                                        {safeText(product?.name, 'Produto sem nome')}
                                    </p>
                                    {safeText(product?.description).trim() && (
                                        <p className="line-clamp-2 text-xs leading-5 text-white/50">
                                            {safeText(product?.description)}
                                        </p>
                                    )}
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-[11px] font-semibold text-white/45">Tocar para pedir</span>
                                        <span className="text-sm font-black" style={{ color: brandPrimary }}>
                                            {product.available ? formatPrice(getProductStartingPrice(product)) : 'Indisponivel'}
                                        </span>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                    {asArray(visibleCategory.products).length === 0 && (
                        <div className="card mt-4 p-8 text-center text-muted-foreground">
                            Nenhum item encontrado nesta categoria.
                        </div>
                    )}
                </section>
            )}

            {!searchTerm.trim() && showcaseProducts.length > 0 && (
                <div className="mb-8 rounded-3xl border border-[#2A2A2A] bg-[#111] p-5">
                    <div>
                        <p className="text-xs uppercase tracking-[0.22em]" style={{ color: brandAccent }}>Ainda em duvida?</p>
                        <p className="mt-1 text-sm text-white/70">
                            Toque em um destaque para comparar opcoes e adicionar ao carrinho.
                        </p>
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                        {showcaseProducts.map((product: any) => (
                            <button
                                key={product.id}
                                type="button"
                                onClick={() => openProductModal(product)}
                                className="overflow-hidden rounded-2xl border border-white/10 bg-[#0E0E0E] text-left transition hover:-translate-y-0.5 hover:border-primary/30"
                            >
                                <div className="aspect-[4/3] bg-[#171717]">
                                    {product.imageUrl ? (
                                        <img src={resolveAssetUrl(product.imageUrl)} alt="" className="h-full w-full object-cover" />
                                    ) : (
                                        <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">IMG</div>
                                    )}
                                </div>
                                <div className="space-y-2 p-3">
                                    <p className="line-clamp-2 text-sm font-semibold text-white">{safeText(product?.name, 'Produto')}</p>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-xs text-white/55">Ver opcoes</span>
                                        <span className="text-sm font-black" style={{ color: brandPrimary }}>
                                            {formatPrice(getProductStartingPrice(product))}
                                        </span>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {reservationNotice && (
                <div className="mb-6 rounded-3xl border border-emerald-500/20 bg-emerald-500/10 p-5 text-sm text-emerald-100">
                    {reservationNotice}
                </div>
            )}

            {(checkoutReturnState || checkoutNotice) && (
                <div className="mb-6 rounded-3xl border p-5" style={{ borderColor: brandSoftBorder, backgroundColor: brandSoftBackground }}>
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div>
                            <p className="text-xs uppercase tracking-[0.22em]" style={{ color: brandAccent }}>Checkout delivery</p>
                            <p className="mt-2 text-lg font-semibold text-white">
                                {checkoutReturnState ? 'Seu pedido esta aguardando pagamento' : 'Pedido confirmado'}
                            </p>
                            <p className="mt-1 text-sm text-white/75">{checkoutNotice}</p>
                            {checkoutReturnState?.total && (
                                <p className="mt-2 text-sm font-semibold" style={{ color: brandPrimary }}>
                                    Total {formatPrice(checkoutReturnState.total)}
                                </p>
                            )}
                            {latestOrderSummary?.shortId && (
                                <div className="mt-4 flex flex-wrap items-center gap-3">
                                    <span className="rounded-full border border-primary/30 bg-[#111] px-3 py-1 text-sm font-bold text-white">
                                        Codigo {latestOrderSummary.shortId}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(latestOrderSummary.shortId!);
                                                setCopiedShortId(true);
                                                window.setTimeout(() => setCopiedShortId(false), 1800);
                                                setCheckoutNotice('Codigo do pedido copiado. Use-o para acompanhar o status.');
                                            } catch (error) {
                                                console.error('Failed to copy order shortId', error);
                                            }
                                        }}
                                        disabled={copiedShortId}
                                        className="inline-flex items-center gap-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-70"
                                        style={{ color: brandPrimary }}
                                    >
                                        <Copy className="h-4 w-4" />
                                        {copiedShortId ? 'Copiado!' : 'Copiar codigo'}
                                    </button>
                                    {slug && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setOpeningTrackedOrderId(latestOrderSummary.orderId);
                                                navigate(`/store/${slug}/pedido/${latestOrderSummary.shortId}`);
                                            }}
                                            disabled={openingTrackedOrderId === latestOrderSummary.orderId}
                                            className="inline-flex items-center gap-2 text-sm font-semibold text-white/85 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {openingTrackedOrderId === latestOrderSummary.orderId ? 'Abrindo...' : 'Abrir acompanhamento'}
                                        </button>
                                    )}
                                    <p className="mx-auto max-w-[220px] text-xs leading-5 text-white/45">
                                        Escolha itens do cardapio para montar seu pedido.
                                    </p>
                                </div>
                            )}
                        </div>
                        {checkoutReturnState && (
                            <div className="flex flex-col gap-2 sm:flex-row">
                                {checkoutReturnState.pixCopyPasteCode ? (
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            try {
                                                await navigator.clipboard.writeText(checkoutReturnState.pixCopyPasteCode || '');
                                                setCopiedPixCode(true);
                                                window.setTimeout(() => setCopiedPixCode(false), 1800);
                                                setCheckoutNotice('Codigo Pix copiado. Agora e so colar no app do banco.');
                                            } catch (error) {
                                                console.error('Failed to copy Pix code', error);
                                            }
                                        }}
                                        disabled={copiedPixCode}
                                        className="btn-primary inline-flex items-center justify-center gap-2 px-4 py-3 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        <Copy className="h-4 w-4" />
                                        {copiedPixCode ? 'Copiado!' : 'Copiar Pix'}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={retryPendingCheckout}
                                        disabled={retryingPendingCheckout}
                                        className="btn-primary inline-flex items-center justify-center gap-2 px-4 py-3 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                        {retryingPendingCheckout ? 'Gerando...' : 'Gerar Pix'}
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={cancelPendingDelivery}
                                    disabled={cancellingPendingDelivery}
                                    className="rounded-xl border border-red-500/20 px-4 py-3 text-sm font-semibold text-red-300 transition hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {cancellingPendingDelivery ? 'Cancelando...' : 'Cancelar delivery'}
                                </button>
                            </div>
                        )}
                    </div>
                    {checkoutReturnState?.pixQrCodeDataUrl && (
                        <div className="mt-5 grid gap-5 border-t pt-5 md:grid-cols-[220px,1fr]" style={{ borderColor: `${brandPrimary}26` }}>
                            <div className="rounded-2xl border border-[#2A2A2A] bg-[#111] p-4">
                                <img
                                    src={checkoutReturnState.pixQrCodeDataUrl}
                                    alt="QR Code Pix"
                                    className="mx-auto h-44 w-44 rounded-xl bg-white p-2"
                                />
                            </div>
                            <div className="space-y-3">
                                <div className="rounded-2xl border border-[#2A2A2A] bg-[#111] p-4">
                                    <p className="text-xs uppercase tracking-[0.22em]" style={{ color: brandAccent }}>Pix Copia e Cola</p>
                                    <p className="mt-2 break-all text-sm text-white/85">{checkoutReturnState.pixCopyPasteCode}</p>
                                </div>
                                {checkoutReturnState.pixExpiresAt && (
                                    <p className="text-xs text-white/60">
                                        Valido ate {new Date(checkoutReturnState.pixExpiresAt).toLocaleString('pt-BR')}
                                    </p>
                                )}
                                {checkoutReturnState.checkoutUrl && (
                                    <a
                                        href={checkoutReturnState.checkoutUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 text-sm font-semibold"
                                        style={{ color: brandPrimary }}
                                    >
                                        Abrir pagina do Pix no Mercado Pago <ExternalLink className="h-4 w-4" />
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {reservationsEnabled && (
                <div className="mb-8 flex flex-col gap-4 rounded-3xl border border-[#2A2A2A] bg-[#111] p-5 md:flex-row md:items-center md:justify-between">
                    <div>
                    <p className="text-xs uppercase tracking-[0.22em]" style={{ color: brandAccent }}>Reservas de mesa</p>
                        <h2 className="mt-2 text-xl font-bold text-white">Prefere reservar antes de chegar?</h2>
                        <p className="mt-1 text-sm text-white/70">
                            Envie sua reserva pelo site e aguarde a confirmacao do restaurante.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            setReservationError('');
                            setReservationModalOpen(true);
                        }}
                        className="btn-primary inline-flex items-center justify-center gap-2 px-4 py-3"
                    >
                        <CalendarDays className="h-4 w-4" />
                        Reservar mesa
                    </button>
                </div>
            )}

            <div className="mb-8 flex flex-col gap-4 rounded-3xl border border-[#2A2A2A] bg-[#111] p-5 md:flex-row md:items-end md:justify-between">
                <div>
                    <p className="text-xs uppercase tracking-[0.22em]" style={{ color: brandAccent }}>Acompanhar pedido</p>
                    <h2 className="mt-2 text-xl font-bold text-white">Ja fez um pedido?</h2>
                    <p className="mt-1 text-sm text-white/70">
                        Digite o codigo curto do pedido para abrir a pagina de acompanhamento.
                    </p>
                </div>
                <div className="flex w-full flex-col gap-3 sm:flex-row md:max-w-xl">
                    <input
                        value={trackShortId}
                        onChange={(event) => setTrackShortId(event.target.value.toUpperCase())}
                        placeholder="Ex.: A1B2C3"
                        className="input flex-1"
                    />
                    <button
                        type="button"
                        onClick={() => {
                            if (!slug || !trackShortId.trim()) return;
                            setOpeningStatusPage(true);
                            navigate(`/store/${slug}/pedido/${trackShortId.trim().toUpperCase()}`);
                        }}
                        disabled={openingStatusPage}
                        className="btn-primary px-5 py-3 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {openingStatusPage ? 'Abrindo...' : 'Ver status'}
                    </button>
                </div>
            </div>

            {customerOrders.length > 0 && (
                <div className="mb-8 rounded-3xl border border-[#2A2A2A] bg-[#111] p-5">
                    <div>
                        <p className="text-xs uppercase tracking-[0.22em]" style={{ color: brandAccent }}>Meus pedidos</p>
                        <h2 className="mt-2 text-xl font-bold text-white">Acompanhe o status do seu pedido</h2>
                        <p className="mt-1 text-sm text-white/70">
                            Este historico fica disponivel automaticamente neste dispositivo.
                        </p>
                    </div>

                    <div className="mt-5 grid gap-4 lg:grid-cols-2">
                        {customerOrders.map((order) => (
                            <div key={order.id} className="rounded-3xl border border-[#2A2A2A] bg-[#0E0E0E] p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.24em]" style={{ color: brandAccent }}>
                                            Pedido {order.shortId}
                                        </p>
                                        <p className="mt-2 text-lg font-bold text-white">
                                            {orderStatusLabels[order.status] || order.status}
                                        </p>
                                    </div>
                                    <span className="rounded-full border border-[#2A2A2A] bg-[#171717] px-3 py-1 text-xs font-semibold text-white">
                                        {orderTypeLabels[order.type] || order.type}
                                    </span>
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2">
                                    {order.paymentMethod && (
                                        <span className="rounded-full bg-[#1A1A1A] px-3 py-1 text-xs text-muted-foreground">
                                            {order.paymentMethod === 'MERCADO_PAGO'
                                                ? 'Mercado Pago'
                                                : order.paymentMethod === 'CARD_ON_DELIVERY'
                                                    ? 'Cartao na entrega'
                                                    : order.paymentMethod === 'CASH'
                                                        ? 'Dinheiro'
                                                        : order.paymentMethod}
                                        </span>
                                    )}
                                    {order.paymentStatus && (
                                        <span className="rounded-full px-3 py-1 text-xs" style={{ backgroundColor: brandSoftBackground, color: brandPrimary }}>
                                            {paymentStatusLabels[order.paymentStatus] || order.paymentStatus}
                                        </span>
                                    )}
                                </div>

                                <div className="mt-4 space-y-2 border-t border-[#232323] pt-4 text-sm">
                                    {asArray(order.items).map((item: any) => (
                                        <div key={item.id} className="flex items-start justify-between gap-3">
                                            <div>
                                                <p className="font-semibold text-white">
                                                    {item.quantity}x {item.product?.name || 'Item'}
                                                </p>
                                                {item.notes && (
                                                    <p className="mt-1 text-xs text-muted-foreground">{item.notes}</p>
                                                )}
                                            </div>
                                            <span className="font-semibold text-white">{formatPrice(item.price * item.quantity)}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-4 flex items-center justify-between border-t border-[#232323] pt-4">
                                    <div className="text-xs text-muted-foreground">
                                        {new Date(order.createdAt).toLocaleString('pt-BR')}
                                    </div>
                                    <div className="text-lg font-black" style={{ color: brandPrimary }}>{formatPrice(order.total)}</div>
                                </div>

                                {order.mercadoPagoCheckoutUrl && order.status === 'AWAITING_PAYMENT' && (
                                    <div className="mt-4 flex flex-wrap gap-4">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setOpeningTrackedOrderId(order.id);
                                                navigate(`/store/${slug}/pedido/${order.shortId}`);
                                            }}
                                            disabled={openingTrackedOrderId === order.id}
                                            className="inline-flex items-center gap-2 text-sm font-semibold text-white/85 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {openingTrackedOrderId === order.id ? 'Abrindo...' : 'Abrir acompanhamento'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setResumingCheckoutOrderId(order.id);
                                                window.location.href = order.mercadoPagoCheckoutUrl!;
                                            }}
                                            disabled={resumingCheckoutOrderId === order.id}
                                            className="inline-flex items-center gap-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                                            style={{ color: brandPrimary }}
                                        >
                                            {resumingCheckoutOrderId === order.id ? 'Abrindo pagamento...' : 'Retomar pagamento'} <ExternalLink className="h-4 w-4" />
                                        </button>
                                    </div>
                                )}

                                {(!order.mercadoPagoCheckoutUrl || order.status !== 'AWAITING_PAYMENT') && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setOpeningTrackedOrderId(order.id);
                                            navigate(`/store/${slug}/pedido/${order.shortId}`);
                                        }}
                                        disabled={openingTrackedOrderId === order.id}
                                        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                                        style={{ color: brandPrimary }}
                                    >
                                        {openingTrackedOrderId === order.id ? 'Abrindo...' : 'Abrir acompanhamento'}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {false && !searchTerm.trim() && showcaseProducts.length > 0 && (
                <div className="mb-8 rounded-3xl border border-[#2A2A2A] bg-[#111] p-5">
                    <div>
                        <p className="text-xs uppercase tracking-[0.22em]" style={{ color: brandAccent }}>Destaques da casa</p>
                        <p className="mt-1 text-sm text-white/70">
                            Comece pelos itens em destaque no cardápio.
                        </p>
                    </div>
                    <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4">
                        {showcaseProducts.map((product: any) => (
                            <button
                                key={product.id}
                                type="button"
                                onClick={() => openProductModal(product)}
                                className="overflow-hidden rounded-2xl border border-white/10 bg-[#0E0E0E] text-left transition hover:-translate-y-0.5 hover:border-primary/30"
                            >
                                <div className="aspect-[4/3] bg-[#171717]">
                                    {product.imageUrl ? (
                                        <img src={resolveAssetUrl(product.imageUrl)} alt="" className="h-full w-full object-cover" />
                                    ) : (
                                        <div className="flex h-full w-full items-center justify-center text-[10px] text-muted-foreground">IMG</div>
                                    )}
                                </div>
                                <div className="space-y-2 p-3">
                                    <p className="line-clamp-2 text-sm font-semibold text-white">{safeText(product?.name, 'Produto')}</p>
                                    <div className="flex items-center justify-between gap-2">
                                        <span className="text-xs text-white/55">{safeText(product?.description).trim() ? 'Abrir detalhes' : 'Ver opcoes'}</span>
                                        <span className="text-sm font-black" style={{ color: brandPrimary }}>
                                            {formatPrice(getProductStartingPrice(product))}
                                        </span>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {false && visibleCategory && (
                <div>
                    <h2 className="text-2xl font-bold text-white mb-5">{visibleCategory.name}</h2>
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                        {(visibleCategory.products ?? []).map((product: any) => (
                            <button
                                key={product.id}
                                onClick={() => openProductModal(product)}
                                disabled={!product.available}
                                className={`text-left transition-all duration-200 ${product.available ? 'cursor-pointer hover:-translate-y-0.5' : 'cursor-not-allowed opacity-60'} `}
                            >
                                <div className="space-y-2">
                                    <div className="aspect-square rounded-xl bg-[#171717] border border-white/10 overflow-hidden shadow-[0_10px_24px_rgba(0,0,0,0.22)]">
                                        {product.imageUrl ? (
                                            <img src={resolveAssetUrl(product.imageUrl)} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground">IMG</div>
                                        )}
                                    </div>
                                    <div className="px-0.5 space-y-1">
                                        <p className="text-[11px] sm:text-xs font-medium leading-tight text-white line-clamp-2 min-h-[30px]">
                                            {safeText(product?.name, 'Produto sem nome')}
                                        </p>
                                        {product.model3dEnabled && product.model3dGlbUrl && (
                                            <div className="space-y-1">
                                                <span
                                                    className="inline-flex rounded-full border px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide"
                                                    style={{
                                                        borderColor: `${storeBranding?.primaryColor || '#ff6b00'}33`,
                                                        backgroundColor: `${storeBranding?.primaryColor || '#ff6b00'}1a`,
                                                        color: storeBranding?.primaryColor || '#ff6b00',
                                                    }}
                                                >
                                                    3D / AR
                                                </span>
                                                <p className="text-[9px] leading-tight text-muted-foreground">
                                                    Toque para abrir o produto e ver em 3D.
                                                </p>
                                            </div>
                                        )}
                                        <p
                                            className={`text-[10px] font-semibold leading-none ${product.available ? '' : 'text-muted-foreground'}`}
                                            style={product.available ? { color: storeBranding?.primaryColor || '#ff6b00' } : undefined}
                                        >
                                            {product.available ? formatPrice(getProductStartingPrice(product)) : 'Indisponível'}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                    {(visibleCategory.products ?? []).length === 0 && (
                        <div className="card p-8 mt-4 text-center text-muted-foreground">
                            Nenhum item encontrado nesta categoria.
                        </div>
                    )}
                </div>
            )}

            {selectedProduct && createPortal(
                <div className="fixed inset-0 z-[120] flex items-stretch justify-center sm:items-center sm:p-4">
                    <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeProductModal} />
                    <div className="relative flex h-[100dvh] w-full max-w-md flex-col overflow-hidden border border-[#2A2A2A] bg-surface animate-slide-in-up sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-3xl">
                        <button
                            onClick={closeProductModal}
                            className="absolute right-4 top-[max(env(safe-area-inset-top),1rem)] z-10 rounded-full border border-[#2A2A2A] bg-[#111]/95 p-2 text-muted-foreground shadow-lg backdrop-blur sm:hidden"
                            aria-label="Fechar produto"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <div className="flex shrink-0 items-start justify-between border-b border-[#2A2A2A] bg-surface px-5 pb-4 pt-[calc(max(env(safe-area-inset-top),1rem)+2.5rem)] sm:px-6 sm:py-5">
                            <div className="space-y-1">
                                <h3 className="text-lg font-bold text-white">{safeText(selectedProduct.name, 'Produto')}</h3>
                                <p className="text-sm text-muted-foreground">{safeText(selectedProduct.description)}</p>
                            </div>
                            <button onClick={closeProductModal} className="ml-3 hidden shrink-0 rounded-xl p-2 text-muted-foreground transition-all hover:bg-[#1A1A1A] sm:block">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6 sm:py-5">
                        <div className="mb-5 space-y-3">
                              {selectedProduct.model3dEnabled && selectedProduct.model3dGlbUrl ? (
                                    <div className="rounded-3xl border border-primary/20 bg-[#111] p-3">
                                        <ProductModelViewer
                                            src={selectedProduct.model3dGlbUrl}
                                            iosSrc={selectedProduct.model3dUsdzUrl}
                                            poster={selectedProduct.imageUrl}
                                            alt={`Modelo 3D de ${safeText(selectedProduct.name, 'produto')}`}
                                            className="rounded-2xl bg-[#0B0B0B] p-2"
                                        />
                                    </div>
                              ) : selectedProduct.imageUrl ? (
                                <div className="overflow-hidden rounded-3xl border border-[#2A2A2A] bg-[#111]">
                                    <img
                                        src={resolveAssetUrl(selectedProduct.imageUrl)}
                                        alt={safeText(selectedProduct.name, 'Produto')}
                                        className="aspect-[4/3] w-full object-cover"
                                    />
                                </div>
                            ) : null}
                        </div>
                        {asArray(selectedProduct.optionGroups).length > 0 && (
                            <div className="space-y-4">
                                {hasHiddenDependentGroups && (
                                    <div className="rounded-xl border border-primary/20 bg-primary/10 px-4 py-3 text-sm text-white/80">
                                        Escolha primeiro o tamanho para liberar os complementos do pedido.
                                    </div>
                                )}
                                {asArray(selectedProduct.optionGroups).map((group) => {
                                    const selectedIds = selectedOptionMap[group.id] || [];
                                    const groupConfig = getEffectiveGroupConfig(group, selectedProductEffectiveRules);

                                    if (!groupConfig.visible) {
                                        return null;
                                    }

                                    return (
                                        <div key={group.id} className="rounded-2xl border border-[#2A2A2A] bg-[#111] p-4 space-y-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <div>
                                                    <p className="text-sm font-semibold text-white">{group.name}</p>
                                                    <p className="text-xs text-muted-foreground">
                                                        {group.required ? 'Obrigatorio' : 'Opcional'} · escolha ate {groupConfig.maxSelect}
                                                    </p>
                                                    {group.pricingMode === 'EXTRA' && groupConfig.includedQuantity > 0 && (
                                                        <p className="mt-1 text-[11px] font-medium text-primary">
                                                            {selectedSizeLabel
                                                                ? `Voce pode escolher ate ${groupConfig.includedQuantity} ${groupConfig.includedQuantity === 1 ? 'item gratis' : 'itens gratis'} neste grupo com o tamanho ${selectedSizeLabel}`
                                                                : `${groupConfig.includedQuantity} ${groupConfig.includedQuantity === 1 ? 'item incluido' : 'itens incluidos'} pelo tamanho escolhido`}
                                                        </p>
                                                    )}
                                                </div>
                                                {group.required && selectedIds.length === 0 && (
                                                    <span className="text-[11px] font-semibold text-primary">Falta escolher</span>
                                                )}
                                            </div>
                                            <div className="space-y-2">
                                                {asArray(group.items).filter((item) => item.available !== false).map((item) => {
                                                    const active = selectedIds.includes(item.id);
                                                    const selectedOption = selectedProductOptionLookup[`${group.id}:${item.id}`];
                                                    const effectivePrice = selectedOption?.effectivePrice ?? item.price;
                                                    const included = !!selectedOption?.included;

                                                    return (
                                                        <button
                                                            key={item.id}
                                                            type="button"
                                                            onClick={() => toggleOptionSelection(group, item.id)}
                                                            className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition-all ${
                                                                active
                                                                    ? 'border-primary bg-primary/10 text-white'
                                                                    : 'border-[#2A2A2A] bg-[#151515] text-white/80'
                                                            }`}
                                                        >
                                                            <div>
                                                                <p className="text-sm font-semibold">{item.name}</p>
                                                                <p className="text-[11px] text-muted-foreground">
                                                                    {group.pricingMode === 'ABSOLUTE'
                                                                        ? formatPrice(item.price)
                                                                        : included
                                                                            ? 'Incluido'
                                                                            : effectivePrice > 0
                                                                                ? `+ ${formatPrice(effectivePrice)}`
                                                                                : 'Sem custo adicional'}
                                                                </p>
                                                            </div>
                                                            <span className={`h-5 w-5 rounded-full border ${active ? 'border-primary bg-primary' : 'border-[#3A3A3A]'}`} />
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                                {productModalError && (
                                    <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                                        {productModalError}
                                    </div>
                                )}
                            </div>
                        )}
                        </div>
                        <div className="space-y-4 border-t border-[#2A2A2A] bg-surface px-5 pb-[calc(max(env(safe-area-inset-bottom),1rem))] pt-4 sm:px-6 sm:py-5">
                        <div className="flex items-center justify-between gap-4">
                            <span className="text-2xl font-black" style={{ color: brandPrimary }}>{formatPrice(selectedProductUnitPrice * qty)}</span>
                            <div className="flex items-center gap-3 bg-[#1A1A1A] rounded-xl p-1">
                                <button onClick={() => setQty((value) => Math.max(1, value - 1))} className="p-2 hover:bg-border rounded-lg transition-all text-muted-foreground hover:text-white">
                                    <Minus className="w-4 h-4" />
                                </button>
                                <span className="w-6 text-center font-bold text-white">{qty}</span>
                                <button onClick={() => setQty((value) => value + 1)} className="p-2 hover:bg-border rounded-lg transition-all text-muted-foreground hover:text-white">
                                    <Plus className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                        <button
                            onClick={handleAdd}
                            className="w-full rounded-2xl py-3 flex items-center justify-center gap-2 font-semibold text-white shadow-[0_18px_40px_rgba(0,0,0,0.22)]"
                            style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandAccent})` }}
                        >
                            <ShoppingCart className="w-5 h-5" />
                            Adicionar ao Carrinho
                        </button>
                        </div>
                    </div>
                </div>,
                document.body,
            )}

            {isOpen && (
                <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-labelledby="store-cart-title">
                    <button
                        type="button"
                        className="absolute inset-0 cursor-default bg-black/70 backdrop-blur-sm"
                        onClick={toggleCart}
                        aria-label="Fechar carrinho"
                    />
                    <div className="relative flex h-dvh w-full max-w-md flex-col overflow-hidden border-l border-white/10 bg-[#0B0B0B] shadow-[-24px_0_70px_rgba(0,0,0,0.45)] animate-slide-in-right">
                        <div className="shrink-0 border-b border-white/10 bg-white/[0.03] px-5 pb-4 pt-[max(env(safe-area-inset-top),1rem)] sm:px-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-[11px] font-black uppercase tracking-[0.24em]" style={{ color: brandAccent }}>
                                        Seu pedido
                                    </div>
                                    <h2 id="store-cart-title" className="mt-1 text-xl font-black text-white">Carrinho</h2>
                                </div>
                                <button
                                    ref={cartCloseButtonRef}
                                    type="button"
                                    onClick={toggleCart}
                                    className="rounded-xl border border-white/10 bg-white/[0.03] p-2.5 text-muted-foreground transition-all hover:bg-white/[0.08] hover:text-white"
                                    aria-label="Fechar carrinho"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="mt-3 flex items-center justify-between rounded-2xl border border-white/8 bg-black/20 px-4 py-3">
                                <div>
                                    <p className="text-xs text-white/55">
                                        {items.length} {items.length === 1 ? 'produto' : 'produtos'} · {cartItemCount} {cartItemCount === 1 ? 'item' : 'itens'}
                                    </p>
                                    <p className="mt-0.5 text-sm font-semibold text-white/85">
                                        {storeBranding?.name || establishment?.name}
                                    </p>
                                </div>
                                <p className="text-lg font-black" style={{ color: brandPrimary }}>{formatPrice(checkoutSubtotal)}</p>
                            </div>
                        </div>
                        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                        <div className="space-y-3 p-4 sm:p-5">
                            {items.length === 0 ? (
                                <div className="space-y-4 rounded-[28px] border border-dashed border-white/12 bg-white/[0.03] px-5 py-12 text-center text-muted-foreground">
                                    <div
                                        className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl"
                                        style={{ backgroundColor: brandSoftBackground, color: brandPrimary }}
                                    >
                                        <ShoppingCart className="w-7 h-7" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-white">Seu carrinho está vazio</p>
                                        <p className="mt-1 text-sm">Escolha um produto do cardápio para começar.</p>
                                    </div>
                                    <button type="button" onClick={toggleCart} className="btn-secondary">
                                        Continuar escolhendo
                                    </button>
                                </div>
                            ) : items.map((item) => (
                                <article key={item.id} className="rounded-2xl border border-white/10 bg-[#111] p-3.5 shadow-[0_12px_30px_rgba(0,0,0,0.18)]">
                                    <div className="flex gap-3">
                                        <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-white/8 bg-[#1A1A1A]">
                                            {item.product.imageUrl ? (
                                                <img src={resolveAssetUrl(item.product.imageUrl)} alt="" className="h-full w-full object-cover" />
                                            ) : (
                                                <div className="flex h-full w-full items-center justify-center text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                                    Sem foto
                                                </div>
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-bold text-white">{item.product.name}</p>
                                                    <p className="mt-0.5 text-xs text-white/45">{formatPrice(item.unitPrice)} cada</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => removeItem(item.id)}
                                                    className="shrink-0 rounded-lg p-2 text-white/35 transition hover:bg-red-500/10 hover:text-red-300"
                                                    aria-label={`Remover ${item.product.name} do carrinho`}
                                                    title="Remover item"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                            {item.selectedOptions.length > 0 && (
                                                <div className="mt-2 space-y-1">
                                                    {item.selectedOptions.map((option) => (
                                                        <p key={`${option.groupId}:${option.itemId}`} className="line-clamp-1 text-[11px] text-muted-foreground">
                                                            <span className="text-white/45">{option.groupName}:</span> {option.itemName}
                                                            {option.included ? ' · incluído' : ''}
                                                        </p>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-3 flex items-center justify-between border-t border-white/8 pt-3">
                                        <div className="flex items-center rounded-xl border border-white/10 bg-black/25 p-1">
                                            <button
                                                type="button"
                                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-white/[0.08] hover:text-white"
                                                aria-label={`Diminuir quantidade de ${item.product.name}`}
                                            >
                                                <Minus className="h-3.5 w-3.5" />
                                            </button>
                                            <span className="w-8 text-center text-sm font-bold text-white">{item.quantity}</span>
                                            <button
                                                type="button"
                                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-white/[0.08] hover:text-white"
                                                aria-label={`Aumentar quantidade de ${item.product.name}`}
                                            >
                                                <Plus className="h-3.5 w-3.5" />
                                            </button>
                                        </div>
                                        <p className="text-base font-black" style={{ color: brandPrimary }}>{formatPrice(item.subtotal)}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                        {items.length > 0 && (
                            <div className="border-t border-white/10 bg-[#0E0E0E] p-4 pb-[calc(max(env(safe-area-inset-bottom),1rem))] sm:p-5">
                                <div className="mb-5 flex items-center justify-between gap-3">
                                    <div>
                                        <p className="text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: brandAccent }}>
                                            Finalizar pedido
                                        </p>
                                        <p className="mt-1 text-sm text-white/55">Preencha os dados abaixo para enviar ao restaurante.</p>
                                    </div>
                                    <span className="shrink-0 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-semibold text-white/70">
                                        Etapa 2 de 2
                                    </span>
                                </div>
                                <div className="space-y-3">
                                <div className="space-y-3">
                                    <div>
                                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                            Tipo do pedido
                                        </label>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { value: 'LOCAL', label: 'Mesa' },
                                                { value: 'PICKUP', label: 'Retirada', disabled: !pickupEnabled },
                                                { value: 'DELIVERY', label: 'Delivery', disabled: !deliveryEnabled || enabledCheckoutPaymentMethods.length === 0 },
                                            ].map((option) => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    disabled={option.disabled}
                                                    onClick={() => {
                                                        setOrderType(option.value as CheckoutOrderType);
                                                        setCheckoutError('');
                                                    }}
                                                    className={`rounded-xl border px-3 py-2 text-sm font-semibold transition-all ${
                                                        orderType === option.value
                                                            ? 'text-white'
                                                            : 'border-[#2A2A2A] bg-[#111] text-muted-foreground hover:text-white'
                                                    } ${option.disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
                                                    style={orderType === option.value ? {
                                                        borderColor: 'transparent',
                                                        background: `linear-gradient(135deg, ${brandPrimary}, ${brandAccent})`,
                                                    } : undefined}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                            Nome do cliente
                                        </label>
                                        <input
                                            value={checkoutData.customerName}
                                            onChange={(event) => {
                                                setCheckoutData((current) => ({ ...current, customerName: event.target.value }));
                                                if (checkoutError) setCheckoutError('');
                                            }}
                                            className="input"
                                            placeholder="Ex.: Joao Silva"
                                        />
                                    </div>
                                    {orderType === 'LOCAL' && (
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                Numero da mesa
                                            </label>
                                            <input
                                                value={checkoutData.tableNumber}
                                                onChange={(event) => {
                                                    setCheckoutData((current) => ({ ...current, tableNumber: event.target.value }));
                                                    if (checkoutError) setCheckoutError('');
                                                }}
                                                className="input"
                                                placeholder="Ex.: 12"
                                                inputMode="numeric"
                                            />
                                        </div>
                                    )}
                                    {(orderType === 'PICKUP' || orderType === 'DELIVERY') && (
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                Telefone {orderType === 'DELIVERY' ? '' : '(opcional)'}
                                            </label>
                                            <input
                                                value={checkoutData.customerPhone}
                                                onChange={(event) => {
                                                    setCheckoutData((current) => ({ ...current, customerPhone: event.target.value }));
                                                    if (checkoutError) setCheckoutError('');
                                                }}
                                                className="input"
                                                placeholder="Ex.: (11) 99999-9999"
                                            />
                                        </div>
                                    )}
                                    {orderType === 'DELIVERY' && (
                                        <>
                                            <div className="grid gap-4 md:grid-cols-2">
                                                <div className="md:col-span-2">
                                                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                        Rua
                                                    </label>
                                                    <input
                                                        value={checkoutData.deliveryStreet}
                                                        onChange={(event) => {
                                                            setCheckoutData((current) => ({ ...current, deliveryStreet: event.target.value }));
                                                            setDeliveryCoverage(null);
                                                            setDeliveryPoint(null);
                                                            setDeliveryPointSource(null);
                                                            if (checkoutError) setCheckoutError('');
                                                        }}
                                                        className="input"
                                                        placeholder="Ex.: Rua Magnolia"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                        Numero da casa ou apartamento
                                                    </label>
                                                    <input
                                                        value={checkoutData.deliveryNumber}
                                                        onChange={(event) => {
                                                            setCheckoutData((current) => ({ ...current, deliveryNumber: event.target.value }));
                                                            if (deliveryPointSource !== 'device' && deliveryPointSource !== 'map') {
                                                                setDeliveryCoverage(null);
                                                                setDeliveryPoint(null);
                                                                setDeliveryPointSource(null);
                                                            }
                                                            if (checkoutError) setCheckoutError('');
                                                        }}
                                                        className="input"
                                                        placeholder="Ex.: 132, apto 302"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                        Bairro
                                                    </label>
                                                    <input
                                                        value={checkoutData.deliveryNeighborhood}
                                                        onChange={(event) => {
                                                            setCheckoutData((current) => ({ ...current, deliveryNeighborhood: event.target.value }));
                                                            setDeliveryCoverage(null);
                                                            setDeliveryPoint(null);
                                                            setDeliveryPointSource(null);
                                                            if (checkoutError) setCheckoutError('');
                                                        }}
                                                        className="input"
                                                        placeholder="Ex.: Centro"
                                                    />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                        Cidade
                                                    </label>
                                                    <input
                                                        value={checkoutData.deliveryCity}
                                                        onChange={(event) => {
                                                            setCheckoutData((current) => ({ ...current, deliveryCity: event.target.value }));
                                                            setDeliveryCoverage(null);
                                                            setDeliveryPoint(null);
                                                            setDeliveryPointSource(null);
                                                            if (checkoutError) setCheckoutError('');
                                                        }}
                                                        className="input"
                                                        placeholder="Ex.: Contagem"
                                                    />
                                                </div>
                                            </div>
                                            <div className="rounded-2xl border border-[#2A2A2A] bg-[#111] p-4 space-y-3">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div>
                                                        <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                            Confirmacao do local da entrega
                                                        </label>
                                                        <p className="text-sm text-white/75">
                                                            Informe o endereco e valide automaticamente. Se precisar, use sua localizacao atual ou ajuste o ponto no mapa.
                                                        </p>
                                                    </div>
                                                    <div className="flex flex-wrap items-center gap-2 justify-end">
                                                        <button
                                                            type="button"
                                                            onClick={useCurrentDeviceLocation}
                                                            disabled={deliveryLocationLoading}
                                                            className="rounded-xl border px-3 py-2 text-sm font-semibold transition disabled:opacity-60"
                                                            style={{ borderColor: brandSoftBorder, color: brandPrimary, backgroundColor: brandSoftBackground }}
                                                        >
                                                            {deliveryLocationLoading ? 'Buscando localizacao...' : 'Usar minha localizacao'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={async () => {
                                                                const formattedAddress = buildDeliveryAddress({
                                                                    street: checkoutData.deliveryStreet,
                                                                    number: checkoutData.deliveryNumber,
                                                                    neighborhood: checkoutData.deliveryNeighborhood,
                                                                    city: checkoutData.deliveryCity,
                                                                });

                                                                if (!formattedAddress) {
                                                                    setCheckoutError('Preencha rua, numero, bairro e cidade para validar automaticamente.');
                                                                    return;
                                                                }
                                                                await checkDeliveryCoverage(
                                                                    {
                                                                        address: formattedAddress,
                                                                        street: checkoutData.deliveryStreet,
                                                                        number: checkoutData.deliveryNumber,
                                                                        neighborhood: checkoutData.deliveryNeighborhood,
                                                                        city: checkoutData.deliveryCity,
                                                                    },
                                                                    {
                                                                        preserveConfirmedPointOnAddressCheck: true,
                                                                        pointSource: 'address',
                                                                    },
                                                                );
                                                            }}
                                                            disabled={deliveryCoverageChecking}
                                                            className="rounded-xl border px-3 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
                                                            style={{ borderColor: brandSoftBorder, color: brandPrimary, backgroundColor: brandSoftBackground }}
                                                        >
                                                            {deliveryCoverageChecking ? 'Validando...' : 'Validar endereco'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setDeliveryMapModalOpen(true)}
                                                            className="rounded-xl border px-3 py-2 text-sm font-semibold transition"
                                                            style={{ borderColor: brandSoftBorder, color: brandPrimary, backgroundColor: brandSoftBackground }}
                                                        >
                                                            <MapPinned className="mr-2 inline h-4 w-4" />
                                                            Abrir mapa
                                                        </button>
                                                    </div>
                                                </div>
                                                {deliveryPoint ? (
                                                    <div className="rounded-xl border border-[#2A2A2A] bg-[#161616] px-3 py-3 text-sm text-white">
                                                        <p>Local da entrega confirmado para a validacao.</p>
                                                        {deliveryCoverage?.geocodedAddress && (
                                                            <p className="mt-2 text-xs text-muted-foreground">{deliveryCoverage.geocodedAddress}</p>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="rounded-xl border border-dashed border-[#2A2A2A] px-3 py-3 text-sm text-muted-foreground">
                                                        Nenhum ponto validado ainda.
                                                    </div>
                                                )}
                                                {deliveryCoverage && (
                                                    <div
                                                        className={`rounded-xl border px-3 py-3 text-sm ${
                                                            deliveryCoverage.deliveryAvailable
                                                                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                                                                : deliveryCoverage.reason === 'validation_failed'
                                                                    ? 'border-amber-500/30 bg-amber-500/10 text-amber-100'
                                                                : 'border-red-500/30 bg-red-500/10 text-red-200'
                                                        }`}
                                                    >
                                                        {deliveryCoverage.deliveryAvailable ? (
                                                            <div className="space-y-1">
                                                                <p>
                                                                    Entrega disponivel nesta regiao
                                                                    {deliveryCoverage.matchedZoneName ? ` (${deliveryCoverage.matchedZoneName})` : ''}.
                                                                </p>
                                                                <p className="text-xs text-emerald-100/80">
                                                                    Taxa aplicada: {formatPrice(Number(deliveryCoverage.deliveryFee) || checkoutDeliveryFee)}
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <div className="space-y-2">
                                                                <p>
                                                                    {deliveryCoverage.reason === 'validation_failed'
                                                                        ? deliveryCoverage.message || 'Nao foi possivel validar esse endereco no mapa.'
                                                                        : 'Esse ponto esta fora da area de entrega configurada.'}
                                                                </p>
                                                                {deliveryCoverage.pickupAvailable && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            setOrderType('PICKUP');
                                                                            setCheckoutError('');
                                                                        }}
                                                                        className="rounded-xl border border-white/15 px-3 py-2 text-sm font-semibold text-white transition hover:bg-white/5"
                                                                    >
                                                                        Trocar para retirada no local
                                                                    </button>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                                {deliveryCoverageChecking && (
                                                    <p className="text-xs" style={{ color: brandPrimary }}>Validando cobertura da entrega...</p>
                                                )}
                                            </div>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                    Forma de pagamento
                                                </label>
                                                <div className="space-y-2">
                                                    {enabledCheckoutPaymentMethods.map((method) => (
                                                        <button
                                                            key={method}
                                                            type="button"
                                                            onClick={() => setDeliveryPaymentMethod(method)}
                                                            className={`w-full rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition-all ${
                                                                deliveryPaymentMethod === method
                                                                    ? 'text-white'
                                                                    : 'border-[#2A2A2A] bg-[#111] text-muted-foreground'
                                                            }`}
                                                            style={deliveryPaymentMethod === method ? {
                                                                borderColor: brandSoftBorder,
                                                                backgroundColor: brandSoftBackground,
                                                            } : undefined}
                                                        >
                                                            {deliveryPaymentLabels[method]}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {deliveryPaymentMethod === 'mercado_pago' && (
                                                <div className="rounded-2xl border px-4 py-3 text-sm text-white/85" style={{ borderColor: brandSoftBorder, backgroundColor: brandSoftBackground }}>
                                                    O sistema vai gerar um Pix com QR Code e codigo copia e cola para voce pagar sem sair da loja.
                                                </div>
                                            )}
                                            {deliveryPaymentMethod === 'card_on_delivery' && (
                                                <div className="rounded-2xl border border-[#2A2A2A] bg-[#111] px-4 py-3 text-sm text-muted-foreground">
                                                    O pedido entra no fluxo normal e o pagamento sera feito com maquininha na entrega.
                                                </div>
                                            )}
                                            {deliveryPaymentMethod === 'cash' && (
                                                <div className="rounded-2xl border border-[#2A2A2A] bg-[#111] p-4 space-y-3">
                                                    <label className="flex items-center gap-3 text-sm text-white">
                                                        <input
                                                            type="checkbox"
                                                            checked={checkoutData.cashNeedsChange}
                                                            onChange={(event) => setCheckoutData((current) => ({
                                                                ...current,
                                                                cashNeedsChange: event.target.checked,
                                                                cashChangeFor: event.target.checked ? current.cashChangeFor : '',
                                                            }))}
                                                            className="w-4 h-4 accent-orange-500"
                                                        />
                                                        Precisa de troco?
                                                    </label>
                                                    {checkoutData.cashNeedsChange && (
                                                        <div>
                                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                                Troco para quanto?
                                                            </label>
                                                            <input
                                                                value={checkoutData.cashChangeFor}
                                                                onChange={(event) => setCheckoutData((current) => ({ ...current, cashChangeFor: event.target.value }))}
                                                                className="input"
                                                                inputMode="decimal"
                                                                placeholder="Ex.: 100"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {orderType === 'PICKUP' && (
                                        <>
                                            <div>
                                                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                    Forma de pagamento
                                                </label>
                                                <div className="space-y-2">
                                                    {enabledCheckoutPaymentMethods.map((method) => (
                                                        <button
                                                            key={method}
                                                            type="button"
                                                            onClick={() => setDeliveryPaymentMethod(method)}
                                                            className={`w-full rounded-2xl border px-4 py-3 text-left text-sm font-semibold transition-all ${
                                                                deliveryPaymentMethod === method
                                                                    ? 'text-white'
                                                                    : 'border-[#2A2A2A] bg-[#111] text-muted-foreground'
                                                            }`}
                                                            style={deliveryPaymentMethod === method ? {
                                                                borderColor: brandSoftBorder,
                                                                backgroundColor: brandSoftBackground,
                                                            } : undefined}
                                                        >
                                                            {method === 'card_on_delivery' ? 'Cartao na retirada' : deliveryPaymentLabels[method]}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            {deliveryPaymentMethod === 'mercado_pago' && (
                                                <div className="rounded-2xl border px-4 py-3 text-sm text-white/85" style={{ borderColor: brandSoftBorder, backgroundColor: brandSoftBackground }}>
                                                    O sistema vai gerar um Pix com QR Code e codigo copia e cola para voce pagar antes de retirar.
                                                </div>
                                            )}
                                            {deliveryPaymentMethod === 'card_on_delivery' && (
                                                <div className="rounded-2xl border border-[#2A2A2A] bg-[#111] px-4 py-3 text-sm text-muted-foreground">
                                                    O pedido entra no fluxo normal e o pagamento sera feito presencialmente na retirada.
                                                </div>
                                            )}
                                            {deliveryPaymentMethod === 'cash' && (
                                                <div className="rounded-2xl border border-[#2A2A2A] bg-[#111] p-4 space-y-3">
                                                    <label className="flex items-center gap-3 text-sm text-white">
                                                        <input
                                                            type="checkbox"
                                                            checked={checkoutData.cashNeedsChange}
                                                            onChange={(event) => setCheckoutData((current) => ({
                                                                ...current,
                                                                cashNeedsChange: event.target.checked,
                                                                cashChangeFor: event.target.checked ? current.cashChangeFor : '',
                                                            }))}
                                                            className="w-4 h-4 accent-orange-500"
                                                        />
                                                        Precisa de troco?
                                                    </label>
                                                    {checkoutData.cashNeedsChange && (
                                                        <div>
                                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                                Troco para quanto?
                                                            </label>
                                                            <input
                                                                value={checkoutData.cashChangeFor}
                                                                onChange={(event) => setCheckoutData((current) => ({ ...current, cashChangeFor: event.target.value }))}
                                                                className="input"
                                                                inputMode="decimal"
                                                                placeholder="Ex.: 100"
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {orderType === 'LOCAL' && (
                                        <div>
                                            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                                Telefone (opcional)
                                            </label>
                                            <input
                                                value={checkoutData.customerPhone}
                                                onChange={(event) => setCheckoutData((current) => ({ ...current, customerPhone: event.target.value }))}
                                                className="input"
                                                placeholder="Ex.: (11) 99999-9999"
                                            />
                                        </div>
                                    )}
                                </div>
                                {checkoutError && (
                                    <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                                        {checkoutError}
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                                        <span>Subtotal dos itens</span>
                                        <span>{formatPrice(checkoutSubtotal)}</span>
                                    </div>
                                    {orderType === 'DELIVERY' && (
                                        <div className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: brandSoftBorder, backgroundColor: brandSoftBackground }}>
                                            <div className="flex items-center justify-between gap-3 text-white">
                                                <span>Taxa de entrega</span>
                                                <span className="font-semibold">{formatPrice(checkoutDeliveryFee)}</span>
                                            </div>
                                            <p className="mt-1 text-xs text-white/75">
                                                Valor fixo cobrado pelo estabelecimento para pedidos delivery.
                                            </p>
                                        </div>
                                    )}
                                        <div className="flex justify-between text-lg font-black">
                                            <span className="text-white">Total</span>
                                            <span style={{ color: brandPrimary }}>{formatPrice(checkoutTotal)}</span>
                                        </div>
                                    </div>
                                <button
                                    onClick={handleCheckout}
                                    disabled={isSubmitting}
                                    className="w-full rounded-2xl py-3 font-semibold text-white shadow-[0_18px_40px_rgba(0,0,0,0.22)] disabled:opacity-50"
                                    style={{ background: `linear-gradient(135deg, ${brandPrimary}, ${brandAccent})` }}
                                >
                                    {isSubmitting ? 'Enviando Pedido...' : (orderType === 'DELIVERY' || orderType === 'PICKUP') && deliveryPaymentMethod === 'mercado_pago' ? 'Gerar Pix' : 'Finalizar Pedido'}
                                </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                </div>
            )}

            {items.length > 0 && !isOpen && (
                <div className="fixed inset-x-4 bottom-[calc(max(env(safe-area-inset-bottom),1rem))] z-40 sm:hidden">
                    <button
                        type="button"
                        onClick={toggleCart}
                        className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-[#101010]/95 px-4 py-3 text-left shadow-[0_24px_60px_rgba(0,0,0,0.35)] backdrop-blur-xl"
                    >
                        <div>
                            <p className="text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: brandAccent }}>
                                Seu pedido
                            </p>
                            <p className="mt-1 text-sm font-semibold text-white">
                                {items.length} {items.length === 1 ? 'item no carrinho' : 'itens no carrinho'}
                            </p>
                        </div>
                        <div className="text-right">
                            <p className="text-lg font-black" style={{ color: brandPrimary }}>{formatPrice(checkoutTotal)}</p>
                            <p className="text-xs text-white/55">Abrir checkout</p>
                        </div>
                    </button>
                </div>
            )}

            <Modal
                isOpen={deliveryMapModalOpen}
                onClose={() => setDeliveryMapModalOpen(false)}
                title="Selecionar ponto da entrega"
                description="Clique no mapa para marcar o local da entrega, caso precise corrigir o endereco encontrado automaticamente."
                footer={(
                    <>
                        <button type="button" onClick={() => setDeliveryMapModalOpen(false)} className="btn-secondary">
                            Fechar
                        </button>
                        <button
                            type="button"
                            onClick={async () => {
                                if (!deliveryPoint) {
                                    setCheckoutError('Selecione um ponto no mapa antes de validar a entrega.');
                                    return;
                                }
                                await checkDeliveryCoverage(deliveryPoint, { pointSource: 'map' });
                                setDeliveryMapModalOpen(false);
                            }}
                            className="btn-primary"
                            disabled={!deliveryPoint || deliveryCoverageChecking}
                        >
                            {deliveryCoverageChecking ? 'Validando...' : 'Validar area'}
                        </button>
                    </>
                )}
            >
                <div className="space-y-4">
                    <DeliveryLocationPicker
                        center={deliveryMapCenter}
                        zones={deliveryZones}
                        selectedPoint={deliveryPoint}
                        onSelect={(coords) => {
                            setDeliveryPoint(coords);
                            setDeliveryPointSource('map');
                            setDeliveryCoverage(null);
                            setCheckoutError('');
                        }}
                    />
                    <div className="rounded-2xl border border-[#2A2A2A] bg-[#111] px-4 py-3 text-sm text-muted-foreground">
                        {deliveryPoint
                            ? 'Local da entrega marcado no mapa.'
                            : 'Clique em qualquer ponto do mapa para marcar o local da entrega.'}
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={reservationModalOpen}
                onClose={() => setReservationModalOpen(false)}
                title="Reservar mesa"
                description="Preencha os dados e envie sua solicitacao para o restaurante."
                footer={(
                    <>
                        <button type="button" onClick={() => setReservationModalOpen(false)} className="btn-secondary">
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            form="storeReservationForm"
                            disabled={reservationSubmitting}
                            className="btn-primary"
                        >
                            {reservationSubmitting ? 'Enviando...' : 'Enviar reserva'}
                        </button>
                    </>
                )}
            >
                <form id="storeReservationForm" onSubmit={handleReservationSubmit} className="space-y-4">
                    <input
                        value={reservationData.customerName}
                        onChange={(event) => setReservationData((current) => ({ ...current, customerName: event.target.value }))}
                        required
                        className="input"
                        placeholder="Nome do cliente"
                    />
                    <input
                        value={reservationData.customerPhone}
                        onChange={(event) => setReservationData((current) => ({ ...current, customerPhone: event.target.value }))}
                        required
                        className="input"
                        placeholder="Telefone"
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <input
                            value={reservationData.date}
                            onChange={(event) => setReservationData((current) => ({ ...current, date: event.target.value }))}
                            required
                            type="date"
                            className="input"
                        />
                        <input
                            value={reservationData.time}
                            onChange={(event) => setReservationData((current) => ({ ...current, time: event.target.value }))}
                            required
                            type="time"
                            className="input"
                        />
                    </div>
                    <input
                        value={reservationData.guests}
                        onChange={(event) => setReservationData((current) => ({ ...current, guests: event.target.value }))}
                        required
                        min="1"
                        type="number"
                        className="input"
                        placeholder="Quantidade de pessoas"
                    />
                    <textarea
                        value={reservationData.notes}
                        onChange={(event) => setReservationData((current) => ({ ...current, notes: event.target.value }))}
                        className="input min-h-24 resize-none"
                        placeholder="Observacoes opcionais"
                    />
                    {reservationError && (
                        <div className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                            {reservationError}
                        </div>
                    )}
                </form>
            </Modal>
        </StoreLayout>
    );
};

export default StorePage;
